import * as THREE from 'three';
import {
  KNEE_AXLES,
  PLATE_FACE,
  hexToWorld,
  kneeAxleAt,
  kneeGearWay,
  kneeJointAt,
  kneesLayout,
  platesLayout,
  platesPhase,
  shellLayout,
  shellTurnsRight,
  tornoAt,
  tornoLayout,
  tornoSpeed,
  type BattleState,
  type KneesLayout,
  type MapDef,
  type ShellLayout,
  type TornoLayout,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { buildTormo, TORMO_PLATES, type TormoBuild } from '../models/tormo.ts';
import { SCALE, WORLD_PER_HEX, facingToRotY } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * Tormo (docs/sendas/artifice.md, chapter 5), for the artificer's path. Its fights take two places other chapters name
 * (`tormoHall` from chapter 1, `rajaWall` from chapter 3): this layer takes a board when its rules carry one of this
 * chapter's acts (`tormoBoard`), and its own places for the scenes.
 *
 * - the knees (`tormoHall` with `knees`): Tormo's cavern coming down by night. Rock walls round a ring of floor (the way
 *   Dunna leads Nadd, its stone worn pale), the roof's beams fallen across the rubble in the middle, lamps on chains.
 *   Tormo walks the ring after them: his four joints' gear trains in sight beside his knees and elbows, tilted up to the
 *   camera, their gears turning as he walks (the first, the drive, under a red arrow), the free axles as iron pins linked
 *   to the two gears each touches. A gear put on the one that jams: the train locks red, sparks, the joint stops; on
 *   another: it spins and sparks. His stomp's ring on the floor (a red band from his knees outward) as he winds it.
 * - the winch (`rajaWall` with `torno`): the winch's ledge across a gorge from Grada's wall. On the far wall, the
 *   quarrymen's houses hanging on their ledges (warm doors), the Raja high over them (black, red deep in it), Tormo on
 *   the wall climbing to it span by span; the cable from the drum across the gorge to his harness, slack or taut as a
 *   harp's string. On the ledge the box (A, B1 and B2 on one shaft, C), its gears by their teeth with the numbers painted
 *   on them, turning while it hauls; the shelf's rack; the lever; the drum.
 * - the shell (`shopYard` with `shell`): the workshop's yard cut in the cliff in the morning, the sea below. Tormo
 *   kneeling tied in its middle, twisting side to side; Nadd's four drills round him on their frames (force plate, red
 *   arrow, clutch lever), the chest's drill before him with a red strap to the one it goes with; the shaft from the mill
 *   and the belts I and II, straight or crossed. A stone that falls leaves the plate under it showing.
 * - the plates (`shopYard` with `plates`): the same yard open. Tormo walking (the sim's ogre drawn as him), the plate
 *   that glows black with a red rim and a glow seen from anywhere; the dark ones grey; the three crossbows on their posts
 *   turning to him and bolting; his fist, stamp and hammer marked on the floor as he winds them; rocks off the cliff.
 * - `shopYard` (the scenes: the yard at dusk, the campfire, the speech, the close) and `gradaRaja` (the epilogue: the
 *   top ledge of Grada under the Raja, the houses below on their ledges).
 *
 * Draw calls: the dressing (one merged mesh), its far rock (one), its bright bits (one), the live Tormo (a mesh a bone,
 * his stones and plates), the gear trains (a few small meshes a joint), the box's five gears, the drills, the
 * crossbows, the discs and rings (pools), the glow layer, the sky. No lights of its own: the board's are repainted.
 * The sim's state and events are read once a tick; frames only move what is there.
 */

export const TORMO_PLACES = new Set(['shopYard', 'gradaRaja', 'tormoDown']);

/** Whether a board is this chapter's (its fights, or its own places). */
export const tormoBoard = (s: BattleState): boolean => {
  const r = s.rules?.senda;
  return !!(r?.knees || r?.torno || r?.shell || r?.plates) || TORMO_PLACES.has(s.rules?.place ?? '');
};

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
/** A deeper red for glows over red (two reds added burn out to pink-white). */
const DEEP = new THREE.Color('#d8200a');
const WARM = new THREE.Color('#ffb48a');
const COLD = new THREE.Color('#bfe0ff');
const SLATE = ['#5d6672', '#535b67', '#68717d', '#4a515c'];
const ROCK = ['#4a515c', '#414852', '#545c67', '#3a4049'];
const WOOD = ['#7d6249', '#6d553f', '#8f7154'];
const IRON = '#454b53';
const BRONZE = '#6a5e50';

type Sky = 'day' | 'dusk' | 'night' | 'dawn';
type Kind = 'knees' | 'hall' | 'torno' | 'shell' | 'plates' | 'yard' | 'raja';

const LOOK: Record<string, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number] }> = {
  cave: { bg: '#222a36', fog: ['#28303d', 26, 80], hemi: ['#b4c4dc', '#3e3a38', 2.4], key: ['#dce6f6', 1.9], rim: ['#ffb07a', 1.2] },
  wall: { bg: '#7d8898', fog: ['#8590a0', 34, 120], hemi: ['#dbe3ee', '#3e4048', 1.8], key: ['#fff0e4', 2.2], rim: ['#b8cdf0', 1.2] },
  morning: { bg: '#7f8ca0', fog: ['#8794a8', 34, 120], hemi: ['#d4dceb', '#3a3c44', 1.35], key: ['#ffeede', 1.85], rim: ['#b4c8ee', 1.1] },
  // The yard's fights: a low morning sun hard across the flags (long shadows, bright tops), the sky's fill cut back.
  yardFight: { bg: '#7a879c', fog: ['#8290a6', 36, 130], hemi: ['#c6d0e0', '#2c2e34', 0.95], key: ['#fff0dc', 2.75], rim: ['#9db8ee', 1.5] },
  dusk: { bg: '#5f6678', fog: ['#666d80', 30, 100], hemi: ['#cfd4e2', '#3a3640', 1.6], key: ['#ffd8c0', 1.9], rim: ['#a8bce8', 1.2] },
  night: { bg: '#273042', fog: ['#2c3548', 26, 90], hemi: ['#a6b6d2', '#36343e', 1.7], key: ['#c4d4f4', 1.5], rim: ['#ffb07a', 1.0] },
};

/** A number painted on a gear's face: pale on dark iron (its teeth). */
const numTex = (text: string, bg = '#1b1f25'): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = bg;
    g.beginPath();
    g.arc(64, 64, 60, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#ffffff';
    g.font = '800 64px Georgia, serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, 64, 68);
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** A curved red arrow (a gear's or a drill's way). */
const arrowTex = (): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.strokeStyle = '#ff4f3a';
    g.lineWidth = 14;
    g.lineCap = 'round';
    g.beginPath();
    g.arc(64, 64, 42, Math.PI * 0.15, Math.PI * 1.55);
    g.stroke();
    g.fillStyle = '#ff4f3a';
    const a = Math.PI * 1.55;
    const x = 64 + Math.cos(a) * 42;
    const y = 64 + Math.sin(a) * 42;
    g.beginPath();
    g.moveTo(x + 24, y - 2);
    g.lineTo(x - 6, y - 20);
    g.lineTo(x - 4, y + 20);
    g.closePath();
    g.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** A gear's outline, extruded (its face toward +Z). */
const gearGeo = (teeth: number): THREE.BufferGeometry => {
  const s = new THREE.Shape();
  for (let i = 0; i < teeth * 2; i++) {
    const a = (i / (teeth * 2)) * Math.PI * 2;
    const a2 = a + Math.PI / (teeth * 2);
    const r = i % 2 === 0 ? 0.5 : 0.42;
    if (i === 0) s.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else s.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    s.lineTo(Math.cos(a2) * r, Math.sin(a2) * r);
  }
  const hole = new THREE.Path();
  hole.absarc(0, 0, 0.1, 0, Math.PI * 2, true);
  s.holes.push(hole);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.1, bevelEnabled: false });
  g.translate(0, 0, -0.05);
  return g;
};

interface TrainView {
  group: THREE.Group;
  gears: THREE.Mesh[];
  arrow: THREE.Mesh;
  pins: THREE.Mesh[];
  spin: number;
  lockMat: THREE.MeshStandardMaterial;
  locked: boolean;
  flash: number;
  spark: number[];
}
interface DrillView {
  g: THREE.Group;
  auger: THREE.Group;
  lever: THREE.Group;
  tip: THREE.Vector3;
  spin: number;
}

export class TormoLayer {
  readonly group = new THREE.Group();
  readonly kind: Kind;
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow = new GlowLayer(320);
  private dome: SkyDome | null = null;
  private now = 0;
  private sky: Sky;
  private open = new Set<string>();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private last: BattleState | null = null;
  private discs: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  /** The stones coming down on the spots (the hall's roof, the cliff over the yard): one a spot, and its trickle of dust. */
  private falling: { stone: THREE.Mesh; dust: THREE.Mesh }[] = [];
  private dustMat: THREE.MeshBasicMaterial | null = null;
  /** The hall's tremor while it is warned: curtains of dust down from the roof all round, a pebble falling in each. */
  private quakeDust: { curtain: THREE.Mesh; pebble: THREE.Mesh; top: number; seed: number }[] = [];
  private quakeMat: THREE.MeshBasicMaterial | null = null;
  private rings: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  /** The live Tormo (the fights). */
  private T: TormoBuild | null = null;
  private stride = 0;
  private lastXY: THREE.Vector3 | null = null;
  private yaw = 0;
  private outStone = [false, false, false];
  private heartCracks: THREE.Mesh[] = [];
  private hurt = 0;
  private twistYaw = 0;
  // The knees.
  private KL: KneesLayout | null = null;
  private trains: TrainView[] = [];
  private handGear: THREE.Mesh | null = null;
  // The winch.
  private TL: TornoLayout | null = null;
  private boxGears: THREE.Group[] = [];
  private gearSpin: number[] = [];
  private cable: THREE.Mesh | null = null;
  private cableMat: THREE.MeshStandardMaterial | null = null;
  private drumAt = new THREE.Vector3();
  private lever: THREE.Group | null = null;
  private pull = 0;
  private slip = 0;
  private climbY = 0;
  private wallZ = -13;
  private drum: THREE.Group | null = null;
  // The shell.
  private SL: ShellLayout | null = null;
  private drills: DrillView[] = [];
  private chest: DrillView | null = null;
  private belts: { straight: THREE.Group; crossed: THREE.Group; lever: THREE.Group }[] = [];
  private mill: THREE.Group | null = null;
  private millSpin = 0;
  // The plates.
  private bows: { g: THREE.Group; arm: THREE.Group; at: THREE.Vector3; jam: number; kick: number }[] = [];
  private bossId = 0;
  private v = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    const r = s.rules?.senda;
    this.kind = r?.knees ? 'knees' : r?.torno ? 'torno' : r?.shell ? 'shell' : r?.plates ? 'plates' : place === 'gradaRaja' ? 'raja' : place === 'tormoDown' ? 'hall' : 'yard';
    this.sky = this.cave ? 'night' : 'day';
    this.group.name = `tormo-${this.kind}`;
    const kit = new Kit();
    const far = new Kit();
    const hot = new Kit();
    if (this.cave) this.buildHall(kit, far, hot, s);
    else if (this.kind === 'torno') this.buildWinch(kit, far, hot, s);
    else if (this.kind === 'raja') this.buildRaja(kit, far, hot);
    else this.buildYard(kit, far, hot, s);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.06, flatShading: true });
    this.mats.push(mat);
    for (const [k, name] of [
      [kit, 'tormo-dress'],
      [far, 'tormo-far'],
    ] as const) {
      const m = k.build(mat, name);
      if (!m) continue;
      if (k === kit) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    const hotMat = new THREE.MeshBasicMaterial({ vertexColors: true, fog: false });
    this.mats.push(hotMat);
    const hm = hot.build(hotMat, 'tormo-hot');
    if (hm) {
      this.geos.push(hm.geometry);
      this.group.add(hm);
    }
    this.pools();
    this.group.add(this.glow.mesh);
    // The live Tormo in the fights (in the plates' he stands where the sim's ogre is).
    if (this.kind === 'knees' || this.kind === 'torno' || this.kind === 'shell' || this.kind === 'plates') this.liveTormo(s);
  }

  /* ---------- helpers ---------- */

  /** Tormo's hall: the knees' fight, or the same hall for the chapter's first scenes (`tormoDown`). */
  private get cave(): boolean {
    return this.kind === 'knees' || this.kind === 'hall';
  }

  private track<T extends THREE.Material | THREE.BufferGeometry | THREE.Texture>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else if (x instanceof THREE.Texture) this.texs.push(x);
    else this.geos.push(x);
    return x;
  }

  private std(color: string, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    return this.track(new THREE.MeshStandardMaterial({ color, roughness: 0.75, metalness: 0.15, flatShading: true, ...o }));
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  private setOpen(cells: { q: number; r: number }[]): void {
    for (const h of cells) this.open.add(`${h.q},${h.r}`);
  }

  private voidAt = (x: number, z: number): boolean => {
    if (!this.open.size) return false;
    const hr = Math.round(z / (S * 0.866));
    const hq = Math.round(x / S - hr / 2);
    for (const [dq, dr] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const) {
      const k = `${hq + dq},${hr + dr}`;
      if (!this.open.has(k)) continue;
      const c = hexToWorld(hq + dq, hr + dr);
      if ((x - c.x * K) ** 2 + (z - c.y * K) ** 2 < (0.62 * S) ** 2) return false;
    }
    return true;
  };

  private carve(terrain: TerrainBuild): void {
    const mesh = terrain.mesh;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const mw = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const idx = geo.index;
    const tri = (i: number): number => (idx ? idx.getX(i) : i);
    const n = idx ? idx.count : pos.count;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
        cx += v.x;
        cz += v.z;
      }
      const out = this.voidAt(cx / 3, cz / 3);
      if (idx) {
        if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
      } else if (out) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        pos.setXYZ(i + 1, x, y, z);
        pos.setXYZ(i + 2, x, y, z);
      }
    }
    if (idx) geo.setIndex(keep);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') o.visible = false;
    });
  }

  private rock(kit: Kit, x: number, z: number, top: number, w: number, k: number, colors: string[], y0 = 0): void {
    const dode = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const j = Math.sin(x * 12.9 + z * 7.3) * 0.5 + 0.5;
    kit.add(dode, colors[Math.floor(j * 3.99) % colors.length] as string, place(x, y0 + top * 0.5 - 0.2, z, j * 3 + k, new THREE.Vector3(w, top + 0.4, w * (0.8 + j * 0.4))));
    if (j > 0.4) kit.add(box, colors[(Math.floor(j * 7) + 1) % colors.length] as string, place(x + 0.2, y0 + top * 0.75, z - 0.15, j * 5, new THREE.Vector3(w * 0.5, top * 0.5, w * 0.45), 0.2, -0.15));
    dode.dispose();
    box.dispose();
  }

  /** Discs (a spot coming: a rock, a fist, a hammer) and rings (a stomp, a stamp, a sweep): pools of eight and four. */
  private pools(): void {
    if (this.kind === 'knees' || this.kind === 'plates') {
      // The tremor's own drawing: over each spot its stone, high and shaking, then down in the last of the warning;
      // a thin fall of dust from where it comes loose. (Eight: the most spots at once.)
      const rockG = this.track(new THREE.DodecahedronGeometry(0.5, 0));
      const rockM = this.std('#454b54', { roughness: 0.95 });
      const dustG = this.track(new THREE.CylinderGeometry(0.06, 0.22, 1, 6, 1, true));
      this.dustMat = this.track(new THREE.MeshBasicMaterial({ color: '#8f97a1', transparent: true, opacity: 0.3, depthWrite: false }));
      for (let i = 0; i < 8; i++) {
        const stone = new THREE.Mesh(rockG, rockM);
        stone.castShadow = true;
        stone.visible = false;
        const dust = new THREE.Mesh(dustG, this.dustMat);
        dust.visible = false;
        this.group.add(stone, dust);
        this.falling.push({ stone, dust });
      }
    }
    const disc = this.track(new THREE.CircleGeometry(1, 32).rotateX(-Math.PI / 2));
    for (let i = 0; i < 8; i++) {
      const mat = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false }));
      const mesh = new THREE.Mesh(disc, mat);
      mesh.visible = false;
      mesh.renderOrder = 3;
      this.group.add(mesh);
      this.discs.push({ mesh, mat });
    }
    const ring = this.track(new THREE.RingGeometry(0.78, 1, 48).rotateX(-Math.PI / 2));
    for (let i = 0; i < 4; i++) {
      // (Not additive: red added over the yard's pale stone came out pink.)
      const mat = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }));
      const mesh = new THREE.Mesh(ring, mat);
      mesh.visible = false;
      mesh.renderOrder = 3;
      this.group.add(mesh);
      this.rings.push({ mesh, mat });
    }
  }

  /** The walls round the open floor: slate blocks taller further out, a second ring of rock behind. */
  private wallsRound(kit: Kit, far: Kit, height: number, colors = SLATE, keepOut?: (q: number, r: number) => boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const R = this.map.radius;
    const ns = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const;
    const isOpen = (q: number, r: number): boolean => this.open.has(`${q},${r}`);
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R || isOpen(q, r) || keepOut?.(q, r)) continue;
        const p = this.hex(q, r, 0);
        const near = ns.some(([dq, dr]) => isOpen(q + dq, r + dr));
        const j = Math.sin(q * 7.7 + r * 3.1) * 0.5 + 0.5;
        if (near) {
          const hgt = height + j * 1.1;
          kit.add(box, colors[(q + r * 3 + 40) % colors.length] as string, place(p.x, hgt / 2 - 0.05, p.z, j * 0.4, new THREE.Vector3(S * 1.02, hgt, S * 1.02), (j - 0.5) * 0.06, (j - 0.5) * 0.06));
          kit.add(box, '#3d434c', place(p.x, hgt + 0.05, p.z, 0, new THREE.Vector3(S * 0.96, 0.1, S * 0.96)));
        } else this.rock(far, p.x, p.z, height + 1.6 + j * 2.2, S * 1.35, q, ROCK);
      }
    box.dispose();
  }

  /**
   * A cliff of slate laid in courses (never columns): long horizontal bands, each a little in or out and of its own
   * shade, from `y0` up to `y1`, its face at `z`, `w` wide.
   */
  private coursed(kit: Kit, z: number, y0: number, y1: number, w: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const shades = ['#56606b', '#4e5762', '#5d6772', '#49525c', '#535d68'];
    let i = 0;
    for (let y = y0; y < y1; y += 1.25, i++) {
      const j = Math.sin(i * 7.3) * 0.5 + 0.5;
      // Each course in three or four long stones, their joints staggered.
      const n = 3 + (i % 2);
      for (let k = 0; k < n; k++) {
        const len = w / n;
        const x = -w / 2 + len * (k + 0.5) + (i % 2 ? len * 0.25 : 0);
        kit.add(box, shades[(i + k * 2) % shades.length] as string, place(x, y + 0.6, z - j * 0.35 - (k % 2) * 0.12, 0, new THREE.Vector3(len - 0.08, 1.18, 2.0)));
      }
    }
    box.dispose();
  }

  /** A lantern hung on a chain (a warm glow). */
  private lamp(kit: Kit, hot: Kit, p: THREE.Vector3, h: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#2a2e34', place(p.x, p.y + h + 0.9, p.z, 0, new THREE.Vector3(0.03, 1.8, 0.03)));
    kit.add(box, '#3a3f46', place(p.x, p.y + h, p.z, 0, new THREE.Vector3(0.2, 0.26, 0.2)));
    hot.add(box, '#ffcfa4', place(p.x, p.y + h, p.z, 0, new THREE.Vector3(0.12, 0.16, 0.12)));
    this.light(new THREE.Vector3(p.x, p.y + h, p.z), WARM, 2.4, 0.55, 0.6);
    box.dispose();
  }

  /* ---------- the knees: Tormo's hall coming down ---------- */

  private buildHall(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L0 = kneesLayout(s.seed);
    // The scenes' hall (`tormoDown`): the same hall before it comes down on the ring; its middle clear (the people
    // stand there), the roof's first stones and a beam down by the walls.
    const scene = this.kind === 'hall';
    const L = scene ? { ...L0, rubble: L0.floor.filter((h) => (Math.abs(h.q) + Math.abs(h.r) + Math.abs(h.q + h.r)) / 2 === 7 && (h.q * 7 + h.r * 3) % 5 === 0) } : L0;
    if (!scene) this.KL = L;
    // (The rubble's cells keep their ground: the heaps stand on it.)
    this.setOpen(scene ? L0.floor.concat(L0.rubble) : [...L.floor, ...L.rubble]);
    const heap = new Set(scene ? [] : L.rubble.map((h) => `${h.q},${h.r}`));
    this.wallsRound(kit, far, 3.6, ['#4b525d', '#434a54', '#545c67', '#3e444d'], (q, r) => heap.has(`${q},${r}`));
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    // The rubble in the middle: heaps of the roof's stone and its beams across them, broken.
    for (const h of L.rubble) {
      const p = this.hex(h.q, h.r, 0);
      const j = Math.sin(h.q * 5.1 + h.r * 2.3) * 0.5 + 0.5;
      this.rock(kit, p.x, p.z, 0.7 + j * 0.8, S * 0.9, h.q + h.r, ['#5f6670', '#555c66', '#6a717b', '#4d535c']);
      this.rock(kit, p.x + 0.35, p.z - 0.3, 0.35, S * 0.45, h.r, ['#6a717b', '#5f6670', '#757c86', '#555c66']);
      if (j > 0.35) kit.add(box, WOOD[Math.floor(j * 2.99)] as string, place(p.x, 0.9 + j * 0.4, p.z, j * 5, new THREE.Vector3(2.4, 0.22, 0.24), 0.25 * (j - 0.5), 0.35));
    }
    // A great beam fallen right across the middle, and the roof's hole over it (a cold shaft of night). In the scenes
    // the roof still holds: its beams overhead, one already down by the west wall.
    if (!scene) {
      kit.add(box, WOOD[0] as string, place(0.4, 1.3, -0.3, 0.7, new THREE.Vector3(6.5, 0.36, 0.4), 0, 0.12));
      kit.add(box, '#2a2e34', place(0.4, 1.52, -0.3, 0.7, new THREE.Vector3(0.5, 0.08, 0.46), 0, 0.12));
    } else {
      for (const z of [-6, -2.4, 1.2, 4.8]) kit.add(box, WOOD[Math.abs(Math.round(z)) % 3] as string, place(0, 5.4, z, 0, new THREE.Vector3(22, 0.4, 0.42)));
      kit.add(box, '#2a2e34', place(0, 5.65, -1, 0, new THREE.Vector3(22, 0.12, 14)));
      kit.add(box, WOOD[0] as string, place(-6.2, 0.7, 1.4, 1.1, new THREE.Vector3(5.2, 0.36, 0.4), 0, 0.28));
      this.rock(kit, -5.4, 2.6, 0.5, S * 0.7, 3, ['#5f6670', '#555c66', '#6a717b', '#4d535c']);
      this.rock(kit, 5.8, -1.8, 0.4, S * 0.6, 5, ['#5f6670', '#555c66', '#6a717b', '#4d535c']);
    }
    // The ring's stone worn pale: a band of flags round the hall, the way Dunna leads Nadd.
    for (const h of L.ring) {
      const p = this.hex(h.q, h.r, 0);
      const j = Math.sin(h.q * 3.9 + h.r * 8.1) * 0.5 + 0.5;
      kit.add(new THREE.CylinderGeometry(S * 0.52, S * 0.54, 0.04, 6), ['#6a717b', '#646b75', '#707781'][Math.floor(j * 2.99)] as string, place(p.x, 0.02, p.z, Math.PI / 6));
    }
    // Scaffolds against the walls, chains hanging, the niche where Tormo sat (north), its wall broken open.
    for (const [q, r] of [
      [-7, 2],
      [6, -5],
      [7, 1],
      [-3, -6],
    ] as const) {
      const p = this.hex(q, r, 0);
      for (const dx of [-0.5, 0.5]) kit.add(box, WOOD[1] as string, place(p.x + dx, 1.6, p.z, 0, new THREE.Vector3(0.12, 3.2, 0.12)));
      for (const y of [1.0, 2.2]) kit.add(box, WOOD[2] as string, place(p.x, y, p.z, 0, new THREE.Vector3(1.3, 0.1, 0.5)));
      kit.add(box, WOOD[0] as string, place(p.x, 1.6, p.z, 0, new THREE.Vector3(1.3, 0.08, 0.08), 0, 0.8));
    }
    for (const [q, r] of [
      [-2, -7],
      [2, -7],
      [-6, 5],
      [5, 4],
      [-7, -2],
      [7, -3],
    ] as const)
      this.lamp(kit, hot, this.hex(q, r, 0), 2.2);
    const n = this.hex(0, -8, 0);
    kit.add(box, '#2c3138', place(n.x, 2.6, n.z + 0.4, 0, new THREE.Vector3(4.2, 5.2, 0.6)));
    kit.add(box, '#14171b', place(n.x, 2.0, n.z + 0.75, 0, new THREE.Vector3(2.6, 4.0, 0.1)));
    // A cold light down the roof's hole (its beams fell: they lie on the rubble).
    this.light(new THREE.Vector3(0.6, 4.5, -0.4), COLD, 5.5, 0.18);
    // Tormo's gear trains: one a joint, beside his knees and elbows (built here, moved by the frame).
    const r = s.rules?.senda?.knees;
    if (r) this.buildTrains(r.joints);
    if (r) {
      // The tremor's dust (hidden till it is warned): a dozen curtains over the ring and the rubble.
      this.quakeMat = this.track(new THREE.MeshBasicMaterial({ color: '#9ea6b0', transparent: true, opacity: 0, depthWrite: false }));
      const cg = this.track(new THREE.CylinderGeometry(0.1, 0.45, 1, 7, 1, true));
      const pg = this.track(new THREE.DodecahedronGeometry(0.12, 0));
      const pm = this.std('#6a717b', { roughness: 1 });
      for (let i = 0; i < 12; i++) {
        const h = L.ring[(i * 7) % L.ring.length] ?? { q: 0, r: 0 };
        const p = this.hex(h.q, h.r, 0);
        const a = i * 2.4;
        const x = p.x * (i % 3 === 0 ? 0.45 : 1) + Math.cos(a) * 0.6;
        const z = p.z * (i % 3 === 0 ? 0.45 : 1) + Math.sin(a) * 0.6;
        const top = 5.4;
        const curtain = new THREE.Mesh(cg, this.quakeMat);
        curtain.position.set(x, top / 2, z);
        curtain.scale.set(1 + (i % 3) * 0.4, top, 1 + (i % 3) * 0.4);
        curtain.visible = false;
        const pebble = new THREE.Mesh(pg, pm);
        pebble.position.set(x, top, z);
        pebble.visible = false;
        this.group.add(curtain, pebble);
        this.quakeDust.push({ curtain, pebble, top, seed: i * 0.37 });
      }
    }
    // The two heaps of gears by the wall (where the hero takes one), bronze in a crate; and the one it carries.
    const ggeo = this.track(gearGeo(10));
    const gm = this.std(BRONZE, { metalness: 0.55, roughness: 0.45 });
    for (const pl of r?.piles ?? []) {
      const p = this.ctx.at(pl.x, pl.y, 0);
      kit.add(box, WOOD[1] as string, place(p.x, 0.22, p.z, 0.3, new THREE.Vector3(1.0, 0.44, 0.8)));
      for (let i = 0; i < 6; i++) {
        const g = new THREE.Mesh(ggeo, gm);
        g.position.set(p.x + Math.cos(i * 2.1) * 0.25, 0.5 + i * 0.07, p.z + Math.sin(i * 2.1) * 0.2);
        g.rotation.set(-Math.PI / 2 + Math.sin(i) * 0.3, 0, i);
        g.scale.setScalar(0.45 - i * 0.03);
        this.group.add(g);
      }
      this.light(p.clone().setY(0.9), WARM, 1.6, 0.3);
    }
    this.handGear = new THREE.Mesh(ggeo, gm);
    this.handGear.scale.setScalar(0.42);
    this.handGear.visible = false;
    this.group.add(this.handGear);
    box.dispose();
    cyl.dispose();
  }

  private buildTrains(joints: { gears: number; drive: number; free: { a: number; b: number }[]; jam: number; off: { x: number; y: number } }[]): void {
    const ggeo = this.track(gearGeo(12));
    const bronze = this.std(BRONZE, { metalness: 0.55, roughness: 0.45 });
    const iron = this.std('#2a2e34', { metalness: 0.4, roughness: 0.6 });
    const arrowMat = this.track(new THREE.MeshBasicMaterial({ map: this.track(arrowTex()), transparent: true, depthWrite: false, side: THREE.DoubleSide }));
    const arrowGeo = this.track(new THREE.PlaneGeometry(1, 1));
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    joints.forEach((J) => {
      const group = new THREE.Group();
      // The train's backing plate and links (merged): the free axles' pins and a bar from each to the two gears it touches.
      const k = new Kit();
      const n = J.gears;
      const gx = (i: number): number => (i - (n - 1) / 2) * 0.27;
      k.add(box, '#30343a', place(0, 0, -0.08, 0, new THREE.Vector3(n * 0.27 + 0.2, 0.62, 0.04)));
      const pins: THREE.Mesh[] = [];
      const lockMat = this.std('#3a3f46', { metalness: 0.5, roughness: 0.4 });
      J.free.forEach((f, i) => {
        const px = (gx(f.a) + gx(f.b)) / 2;
        const py = i % 2 === 0 ? 0.3 : -0.3;
        for (const gi of [f.a, f.b]) {
          const dx = gx(gi) - px;
          const dy = 0 - py;
          const len = Math.hypot(dx, dy);
          k.add(box, '#9aa3ae', place(px + dx / 2, py + dy / 2, -0.02, 0, new THREE.Vector3(0.03, len, 0.02), 0, Math.atan2(-dx, dy)));
        }
        const pin = new THREE.Mesh(cyl, lockMat);
        pin.rotation.x = Math.PI / 2;
        pin.position.set(px, py, 0.02);
        pin.scale.set(0.09, 0.14, 0.09);
        group.add(pin);
        pins.push(pin);
      });
      const back = k.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0.3, flatShading: true })), 'tormo-train');
      if (back) {
        this.geos.push(back.geometry);
        group.add(back);
      }
      const gears: THREE.Mesh[] = [];
      for (let i = 0; i < n; i++) {
        const gm = new THREE.Mesh(ggeo, bronze);
        gm.position.set(gx(i), 0, 0);
        gm.scale.setScalar(0.3);
        group.add(gm);
        gears.push(gm);
      }
      // The drive's red arrow, over the first gear.
      const arrow = new THREE.Mesh(arrowGeo, arrowMat);
      arrow.position.set(gx(0), 0, 0.06);
      arrow.scale.set(0.36 * J.drive, 0.36, 1);
      group.add(arrow);
      group.scale.setScalar(1.25);
      this.group.add(group);
      this.trains.push({ group, gears, arrow, pins, spin: 0, lockMat, locked: false, flash: 0, spark: J.free.map(() => 0) });
      void iron;
    });
    box.dispose();
  }

  /* ---------- the winch: the ledge across the gorge from Grada ---------- */

  private buildWinch(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = tornoLayout(s.seed);
    this.TL = L;
    this.setOpen(L.floor);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 14);
    const R = this.map.radius;
    // The ledge's own rock below its rim and the cliff behind it (west and east ends), the drop under the north rim.
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R || this.open.has(`${q},${r}`)) continue;
        const p = this.hex(q, r, 0);
        const j = Math.sin(q * 7.7 + r * 3.1) * 0.5 + 0.5;
        // East and west: the rock the ledge is cut in (the drop north and south is open).
        if (Math.abs(p.x) > 7.6 && Math.abs(p.z) < 6) this.rock(far, p.x, p.z, 2.6 + j * 2.4, S * 1.4, q, ROCK);
      }
    // The ledge's cliff face down into the gorge (north) and the abyss (south).
    for (let x = -9; x <= 9; x += 1.4) {
      const j = Math.sin(x * 9.1) * 0.5 + 0.5;
      far.add(box, ROCK[Math.floor(j * 3.99)] as string, place(x, -5, -3.9, 0, new THREE.Vector3(1.6, 10, 1.2)));
      far.add(box, ROCK[Math.floor(j * 3.99 + 1) % 4] as string, place(x, -5, 4.6, 0, new THREE.Vector3(1.6, 10, 1.2)));
    }
    // Grada's wall across the gorge: courses of slate, its ledges with the quarrymen's houses (warm doors), the Raja.
    const wz = this.wallZ;
    this.coursed(far, wz - 0.8, -16, 18, 50);
    for (let li = 0; li < 4; li++) {
      const ly = -7.5 + li * 2.6;
      kit.add(box, '#59606a', place(0, ly - 0.3, wz + 0.9, 0, new THREE.Vector3(26, 0.5, 2.0)));
      for (let i = 0; i < 9; i++) {
        const hx = -11 + i * 2.7 + (li % 2) * 1.2;
        const j = Math.sin(hx * 3.1 + li) * 0.5 + 0.5;
        kit.add(box, ['#84888e', '#74787e', '#8e9298'][(i + li) % 3] as string, place(hx, ly + 0.55, wz + 0.7, 0, new THREE.Vector3(1.5, 1.1, 1.0)));
        for (const sg of [-1, 1]) kit.add(box, '#2d3138', place(hx, ly + 1.28, wz + 0.7 + sg * 0.26, 0, new THREE.Vector3(1.65, 0.1, 0.66), sg * 0.6, 0));
        if (j > 0.25) {
          hot.add(box, '#ffc89e', place(hx + 0.3, ly + 0.6, wz + 1.22, 0, new THREE.Vector3(0.28, 0.36, 0.03)));
          this.light(new THREE.Vector3(hx + 0.3, ly + 0.6, wz + 1.5), WARM, 1.1, 0.5, 0.5);
        }
      }
      // A stair between ledges.
      for (let k = 0; k < 6; k++) kit.add(box, '#666c75', place(-1.5 + li * 1.1 + k * 0.32, ly + 0.2 + k * 0.4, wz + 1.0, 0, new THREE.Vector3(0.5, 0.12, 0.7)));
    }
    // The Raja: a black crack along the wall high over the houses, red deep in it.
    for (let x = -18; x < 18; x += 0.8) {
      const yy = 9.4 + Math.sin(x * 1.9) * 0.4 + Math.sin(x * 0.37) * 0.9;
      const wid = 0.45 + (Math.abs(x) < 5 ? 0.6 : 0.2) + (Math.sin(x * 0.7) * 0.5 + 0.5) * 0.4;
      kit.add(box, '#06070a', place(x, yy, wz + 0.5, 0, new THREE.Vector3(1.0, wid, 0.4), 0, Math.cos(x * 1.9) * 0.5));
      if (Math.floor(x / 0.8) % 2 === 0) {
        hot.add(box, '#8a1e10', place(x, yy, wz + 0.42, 0, new THREE.Vector3(0.7, wid * 0.4, 0.2), 0, Math.cos(x * 1.9) * 0.5));
        this.light(new THREE.Vector3(x, yy, wz + 0.9), '#c2361e', 1.2 + wid, 0.42, 0.3);
      }
    }
    // The gorge's floor far below, dark, so the drop reads deep (not sky).
    const deep = new THREE.Mesh(this.track(new THREE.PlaneGeometry(120, 120)), this.std('#2a313b', { roughness: 1 }));
    deep.rotation.x = -Math.PI / 2;
    deep.position.set(0, -16, 0);
    this.group.add(deep);
    // Mist low in the gorge.
    const mist = new THREE.Mesh(this.track(new THREE.PlaneGeometry(60, 12)), this.track(new THREE.MeshBasicMaterial({ color: '#b8c4d2', transparent: true, opacity: 0.35, depthWrite: false, fog: true })));
    mist.rotation.x = -Math.PI / 2;
    mist.position.set(0, -8, -8);
    this.group.add(mist);
    // The box: an iron bed on timber, the slots' posts; B1 and B2 on one shaft (a bar between their cells).
    const slotAt = L.slots.map((h) => this.hex(h.q, h.r, 0));
    const a0 = slotAt[0] as THREE.Vector3;
    const c0 = slotAt[3] as THREE.Vector3;
    const mid = a0.clone().lerp(c0, 0.5);
    kit.add(box, WOOD[1] as string, place(mid.x, 0.18, mid.z, Math.atan2(-(c0.z - a0.z), c0.x - a0.x), new THREE.Vector3(a0.distanceTo(c0) + 1.4, 0.36, 1.3)));
    kit.add(box, IRON, place(mid.x, 0.4, mid.z, Math.atan2(-(c0.z - a0.z), c0.x - a0.x), new THREE.Vector3(a0.distanceTo(c0) + 1.2, 0.08, 1.1)));
    for (const p of slotAt) kit.add(cyl, IRON, place(p.x, 0.75, p.z, 0, new THREE.Vector3(0.12, 0.8, 0.12)));
    const b1 = slotAt[1] as THREE.Vector3;
    const b2 = slotAt[2] as THREE.Vector3;
    kit.add(box, '#7a828c', place((b1.x + b2.x) / 2, 1.05, (b1.z + b2.z) / 2, Math.atan2(-(b2.z - b1.z), b2.x - b1.x), new THREE.Vector3(b1.distanceTo(b2), 0.1, 0.1)));
    // The shelf: a rack of timber with a peg a gear.
    const lie = L.lie.map((h) => this.hex(h.q, h.r, 0));
    const l0 = lie[0] as THREE.Vector3;
    const l4 = lie[4] as THREE.Vector3;
    const lm = l0.clone().lerp(l4, 0.5);
    kit.add(box, WOOD[0] as string, place(lm.x, 0.5, lm.z - 0.42, 0, new THREE.Vector3(l0.distanceTo(l4) + 1.2, 1.0, 0.16)));
    kit.add(box, WOOD[2] as string, place(lm.x, 0.08, lm.z, 0, new THREE.Vector3(l0.distanceTo(l4) + 1.2, 0.16, 1.0)));
    for (const p of lie) kit.add(cyl, IRON, place(p.x, 0.6, p.z - 0.3, 0, new THREE.Vector3(0.06, 0.7, 0.06), Math.PI / 2, 0));
    // The drum: a timber drum on its frame, the cable wound on it.
    this.drumAt = this.ctx.at(L.drum.x, L.drum.y, 1.0);
    const drum = new THREE.Group();
    const dk = new Kit();
    dk.add(cyl, WOOD[2] as string, place(0, 0, 0, 0, new THREE.Vector3(1.0, 1.4, 1.0), 0, Math.PI / 2));
    for (const x of [-0.5, 0, 0.5]) dk.add(cyl, '#5b5f66', place(x, 0, 0, 0, new THREE.Vector3(1.06, 0.12, 1.06), 0, Math.PI / 2));
    dk.add(cyl, '#8d8a84', place(0, 0, 0, 0, new THREE.Vector3(1.04, 1.0, 1.04), 0, Math.PI / 2));
    const dm = dk.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0.2, flatShading: true })), 'tormo-drum');
    if (dm) {
      this.geos.push(dm.geometry);
      drum.add(dm);
    }
    drum.position.copy(this.drumAt);
    this.group.add(drum);
    this.drum = drum;
    for (const dx of [-0.85, 0.85]) kit.add(box, WOOD[1] as string, place(this.drumAt.x + dx, 0.55, this.drumAt.z, 0, new THREE.Vector3(0.18, 1.1, 0.5)));
    // The lever.
    const lp = this.hex(L.lever.q, L.lever.r, 0);
    kit.add(box, IRON, place(lp.x, 0.15, lp.z, 0, new THREE.Vector3(0.6, 0.3, 0.6)));
    const lever = new THREE.Group();
    const lk = new Kit();
    lk.add(box, IRON, place(0, 0.5, 0, 0, new THREE.Vector3(0.1, 1.0, 0.1)));
    lk.add(box, '#ff4f3a', place(0, 1.02, 0, 0, new THREE.Vector3(0.2, 0.2, 0.2)));
    const lm2 = lk.build(this.std('#ffffff', { vertexColors: true }), 'tormo-lever');
    if (lm2) {
      this.geos.push(lm2.geometry);
      lever.add(lm2);
    }
    lever.position.set(lp.x, 0.3, lp.z);
    this.group.add(lever);
    this.lever = lever;
    // The cable: one stretched cylinder from the drum to Tormo's harness (set each frame).
    this.cableMat = this.std('#c9ccd0', { metalness: 0.6, roughness: 0.35, emissive: '#3a3f46' });
    this.cable = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.05, 0.05, 1, 6)), this.cableMat);
    this.group.add(this.cable);
    // The gears: one each, numbered with its teeth, sized by them.
    const r = s.rules?.senda?.torno;
    const gmat = this.std(BRONZE, { metalness: 0.55, roughness: 0.45 });
    for (const teeth of r?.pieces ?? L.set.pieces) {
      const grp = new THREE.Group();
      const gg = new THREE.Mesh(this.track(gearGeo(Math.round(teeth / 2))), gmat);
      const sz = 0.5 + teeth * 0.024;
      gg.scale.set(sz, sz, 1.2);
      grp.add(gg);
      const face = new THREE.Mesh(this.track(new THREE.CircleGeometry(0.26, 24)), this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(teeth))) })));
      face.position.z = 0.08;
      grp.add(face);
      // (Leaning back, its face up to the south: the table camera reads its number from above, on a phone too.)
      grp.rotation.x = -0.9;
      this.group.add(grp);
      this.boxGears.push(grp);
      this.gearSpin.push(0);
    }
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the workshop's yard ---------- */

  private buildYard(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const shell = this.kind === 'shell';
    const L = shell ? shellLayout(s.seed) : null;
    const P = this.kind === 'plates' ? platesLayout() : null;
    if (L) this.SL = L;
    const floor = L?.floor ?? P?.floor ?? [];
    if (floor.length) this.setOpen(floor);
    else {
      // The scenes' board: everything within its radius is yard.
      const R = this.map.radius - 1;
      const cells: { q: number; r: number }[] = [];
      for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) cells.push({ q, r });
      this.setOpen(cells);
    }
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    const R = this.map.radius;
    // North: the cliff the workshop is cut in (a carved front, its door and windows warm). South: the rim and the sea.
    let northZ = 0;
    for (const k of this.open) {
      const [q, r] = k.split(',').map(Number) as [number, number];
      const z = this.hex(q, r, 0).z;
      if (z < northZ) northZ = z;
    }
    const wz = northZ - S * 1.1;
    for (let x = -18; x <= 18; x += 1.6) {
      const j = Math.sin(x * 12.9) * 0.5 + 0.5;
      far.add(box, ['#646b75', '#5a616b', '#6e757f'][Math.floor(j * 2.99)] as string, place(x, 6, wz - 0.5 - j * 0.6, 0, new THREE.Vector3(1.8, 14, 1.6)));
    }
    // The workshop's front: a broad arch of worked stone, its doors, two windows lit, the forge's chimney smoking.
    kit.add(box, '#7c838d', place(0, 2.4, wz + 0.5, 0, new THREE.Vector3(7.2, 4.8, 0.6)));
    kit.add(box, '#262a30', place(0, 1.6, wz + 0.82, 0, new THREE.Vector3(2.6, 3.2, 0.08)));
    kit.add(box, '#8a919b', place(0, 3.35, wz + 0.86, 0, new THREE.Vector3(3.0, 0.3, 0.12)));
    for (const x of [-2.6, 2.6]) {
      kit.add(box, '#2b2f35', place(x, 2.6, wz + 0.82, 0, new THREE.Vector3(0.9, 1.1, 0.06)));
      hot.add(box, '#ffc49a', place(x, 2.6, wz + 0.84, 0, new THREE.Vector3(0.7, 0.9, 0.02)));
      this.light(new THREE.Vector3(x, 2.6, wz + 1.2), WARM, 2.0, 0.45, 0.4);
    }
    // Nadd's mark cut over the door (a ring and its stroke), in red.
    hot.add(new THREE.TorusGeometry(0.32, 0.06, 6, 20), '#d8432c', place(0, 4.15, wz + 0.82, 0));
    hot.add(box, '#d8432c', place(0, 3.75, wz + 0.82, 0, new THREE.Vector3(0.1, 0.5, 0.04)));
    kit.add(box, '#4b5058', place(3.8, 5.2, wz + 0.2, 0, new THREE.Vector3(0.9, 4.0, 0.9)));
    // The forge at the yard's west under its hood, red inside.
    const fg = this.hex(-6, 0, 0);
    if (Math.abs(fg.x) < R * S) {
      kit.add(box, '#4a4f57', place(fg.x - 1.2, 0.55, wz + 1.4, 0, new THREE.Vector3(2.0, 1.1, 1.2)));
      hot.add(box, '#ff5a32', place(fg.x - 1.2, 0.9, wz + 2.02, 0, new THREE.Vector3(1.2, 0.3, 0.02)));
      this.light(new THREE.Vector3(fg.x - 1.2, 1.0, wz + 2.3), RED, 2.6, 0.6, 0.5);
    }
    // The rim: a low parapet of worked stone round the south, gaps where the stair goes down; the sea far below.
    for (const k of this.open) {
      const [q, r] = k.split(',').map(Number) as [number, number];
      for (const [dq, dr] of [
        [0, 1],
        [-1, 1],
        [1, 0],
        [-1, 0],
      ] as const) {
        if (this.open.has(`${q + dq},${r + dr}`)) continue;
        const a = this.hex(q, r, 0);
        const bb = this.hex(q + dq, r + dr, 0);
        if (bb.z < a.z - 0.3) continue;
        const m = a.clone().lerp(bb, 0.5);
        const j = Math.sin(q * 4.1 + r * 9.7) * 0.5 + 0.5;
        const yaw = Math.atan2(-(bb.z - a.z), bb.x - a.x) + Math.PI / 2;
        // The cliff's lip under the rim: broken rock jutting out past it, a little lower.
        const out = bb.clone().sub(a).setY(0).normalize();
        this.rock(kit, m.x + out.x * 0.55, m.z + out.z * 0.55, 0.5 + j * 0.4, S * 0.75, q * 3 + r, ROCK, -0.75);
        if (j < 0.12) {
          // A gap: the parapet's stone fallen inward, tilted on the flags.
          kit.add(box, '#686f78', place(m.x - out.x * 0.5, 0.18, m.z - out.z * 0.5, yaw + 0.4, new THREE.Vector3(S * 0.5, 0.3, 0.34), 0.3, 0.25));
          continue;
        }
        kit.add(box, ['#6c737c', '#646b74', '#747b84'][Math.floor(j * 2.99)] as string, place(m.x, 0.32, m.z, yaw, new THREE.Vector3(S * 0.62, 0.64, 0.34)));
        kit.add(box, '#8a919a', place(m.x, 0.655, m.z, yaw, new THREE.Vector3(S * 0.64, 0.05, 0.38)));
        // Under it, the cliff face down to the sea.
        far.add(box, ROCK[(q + 40) % 4] as string, place(m.x, -4.5, m.z + 0.3, 0, new THREE.Vector3(S * 1.2, 9, 1.0)));
        // The cliff in strata under the yard: two ledges stepping out, their tops paler (else one plain dark wall).
        for (const [y, o] of [
          [-2.2, 0.35],
          [-5.4, 0.6],
        ] as const) {
          const jj = Math.sin(q * 2.3 + r * 5.1 + y) * 0.5 + 0.5;
          far.add(box, ROCK[(q + r + 41) % 4] as string, place(m.x + out.x * o, y - jj * 0.4, m.z + out.z * o, jj, new THREE.Vector3(S * 1.25, 0.9 + jj * 0.6, 0.9)));
          far.add(box, '#6b727c', place(m.x + out.x * o, y - jj * 0.4 + 0.47 + jj * 0.3, m.z + out.z * o, jj, new THREE.Vector3(S * 1.2, 0.06, 0.88)));
        }
      }
    }
    this.flagsAndRubble(kit, hot);
    this.rajaOnCliff(kit, hot, wz);
    const sea = new THREE.Mesh(this.track(new THREE.PlaneGeometry(140, 80)), this.std('#2b465c', { roughness: 0.25, metalness: 0.2 }));
    sea.rotation.x = -Math.PI / 2;
    sea.position.set(0, -9, 30);
    this.group.add(sea);
    // Stone blocks about the yard, a rack of tools, a cart of iron (the scenes' corners).
    for (const [q, r] of [
      [-6, 1],
      [5, -6],
      [6, 2],
    ] as const) {
      const p = this.hex(q, r, 0);
      if (!this.open.has(`${q},${r}`)) this.rock(kit, p.x, p.z, 0.9, S * 0.9, q, ['#7b8189', '#6f757d', '#868c94', '#646a72']);
    }
    // The crossbows' posts (the fight), the drills' line and its mill (the shell).
    if (P) this.buildBows(kit, P.bows);
    if (L && shell) this.buildDrills(kit, hot, s, L);
    box.dispose();
    cyl.dispose();
  }

  /**
   * The yard's flags: slate laid in courses (never the board's grey hexes), each slab its own shade and a little proud of
   * the dark ground, a few cracked or chipped; heaps of broken stone and slabs in the corners and along the rim.
   * All in the dressing's one mesh.
   */
  private flagsAndRubble(kit: Kit, hot: Kit): void {
    void hot;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const shades = ['#757b84', '#686e77', '#828891', '#5f656e', '#7b7f86', '#6a7078', '#8a9097'];
    const R = this.map.radius;
    const ext = (R + 1) * S;
    const W = 1.12;
    const D = 0.8;
    const fr = (x: number, z: number): number => {
      const v = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
      return v - Math.floor(v);
    };
    let zi = 0;
    for (let z = -ext; z < ext; z += D, zi++) {
      const off = (zi % 2) * W * 0.5;
      for (let x = -ext + off; x < ext; x += W) {
        const f = fr(x, z);
        // A long slab now and then (two courses' worth), as a yard is laid.
        const w = W - 0.07;
        const d = D - 0.07;
        const hw = w / 2;
        const hd = d / 2;
        if (this.voidAt(x - hw, z - hd) || this.voidAt(x + hw, z - hd) || this.voidAt(x - hw, z + hd) || this.voidAt(x + hw, z + hd)) continue;
        const h = 0.05 + f * 0.03;
        const col = shades[Math.floor(f * 6.99)] as string;
        kit.add(box, col, place(x, h / 2, z, (f - 0.5) * 0.04, new THREE.Vector3(w, h, d), (f - 0.5) * 0.03, (fr(z, x) - 0.5) * 0.03));
        // Worn pale where feet went (the middle), a crack across some, a chipped corner on others.
        if (f > 0.93) kit.add(box, '#41464d', place(x + (f - 0.9) * 2, h + 0.002, z, f * 9, new THREE.Vector3(w * 0.6, 0.006, 0.025)));
        else if (f < 0.08) kit.add(box, '#4a4f56', place(x + hw - 0.12, h + 0.002, z + hd - 0.1, 0.7, new THREE.Vector3(0.2, 0.008, 0.16)));
      }
    }
    // Rubble: heaps of broken stone and a split slab or two, in a ring near the rim and in the corners.
    const cells = [...this.open].map((k) => k.split(',').map(Number) as [number, number]);
    const len = (q: number, r: number): number => (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;
    let n = 0;
    for (const [q, r] of cells) {
      const f = fr(q * 1.7, r * 2.3);
      if (len(q, r) < R - 4 || f > 0.16 || n > 16) continue;
      n++;
      const p = this.hex(q, r, 0);
      this.rock(kit, p.x, p.z, 0.28 + f * 1.2, S * 0.5, q + r, ['#6a717b', '#5f6670', '#757c86', '#555c66'], 0.02);
      this.rock(kit, p.x + 0.4, p.z + 0.2, 0.16, S * 0.28, r, ['#757c86', '#6a717b', '#5f6670', '#81888f'], 0.02);
      kit.add(box, shades[n % shades.length] as string, place(p.x - 0.35, 0.14, p.z - 0.25, f * 20, new THREE.Vector3(0.7, 0.07, 0.5), 0.35, 0.2));
    }
    box.dispose();
  }

  /**
   * The Raja up on the cliff over the workshop: a black split zig-zagging across the face, red deep in it, its lips of
   * paler broken rock (where the shoring went in).
   */
  private rajaOnCliff(kit: Kit, hot: Kit, wz: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const z = wz + 0.45;
    for (let x = -15; x < 15; x += 0.45) {
      const y = 4.3 + x * 0.1 + Math.sin(x * 1.7) * 0.35 + Math.sin(x * 0.43) * 0.5;
      // Widest over the workshop, thinning to a hair at its ends; its segments overlap so it reads as one split.
      const wid = 0.08 + 0.5 * Math.max(0, 1 - Math.abs(x + 2) / 13) * (0.75 + 0.25 * Math.sin(x * 2.3));
      const tz = Math.cos(x * 1.7) * 0.4;
      kit.add(box, '#8e949c', place(x, y + wid * 0.5 + 0.05, z - 0.04, 0, new THREE.Vector3(0.7, 0.08, 0.2), 0, tz));
      kit.add(box, '#06070a', place(x, y, z, 0, new THREE.Vector3(0.7, wid, 0.14), 0, tz));
      hot.add(box, '#a2220d', place(x, y, z + 0.06, 0, new THREE.Vector3(0.66, wid * 0.28, 0.04), 0, tz));
      if (Math.floor((x + 15) / 0.45) % 8 === 0) this.light(new THREE.Vector3(x, y, z + 0.4), DEEP, 1.2 + wid * 2, 0.3, 0.5);
    }
    box.dispose();
  }

  private buildBows(kit: Kit, at: { x: number; y: number }[]): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const b of at) {
      const p = this.ctx.at(b.x, b.y, 0);
      kit.add(box, WOOD[0] as string, place(p.x, 0.5, p.z, 0, new THREE.Vector3(0.24, 1.0, 0.24)));
      kit.add(box, WOOD[1] as string, place(p.x, 0.06, p.z, 0, new THREE.Vector3(0.9, 0.12, 0.9)));
      const g = new THREE.Group();
      const arm = new THREE.Group();
      const k = new Kit();
      k.add(box, WOOD[2] as string, place(0, 0, 0.2, 0, new THREE.Vector3(0.18, 0.18, 1.5)));
      k.add(box, '#2f343b', place(0, 0.02, 0.8, 0, new THREE.Vector3(1.6, 0.08, 0.1)));
      k.add(box, '#cfd5dc', place(0, 0.09, 0.4, 0, new THREE.Vector3(0.04, 0.04, 1.0)));
      k.add(box, '#ff4f3a', place(0, 0.1, 0.95, 0, new THREE.Vector3(0.08, 0.08, 0.14)));
      const m = k.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.15, flatShading: true })), 'tormo-bow');
      if (m) {
        this.geos.push(m.geometry);
        arm.add(m);
      }
      g.add(arm);
      g.position.set(p.x, 1.1, p.z);
      this.group.add(g);
      this.bows.push({ g, arm, at: p.clone().setY(1.1), jam: 0, kick: 0 });
    }
    box.dispose();
  }

  private drillModel(force: number, mark?: boolean): DrillView {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const g = new THREE.Group();
    const k = new Kit();
    // The frame: two legs and a head, the auger's sleeve, the force's plate, the clutch's post.
    for (const dx of [-0.42, 0.42]) k.add(box, WOOD[1] as string, place(dx, 0.7, -0.2, 0, new THREE.Vector3(0.14, 1.4, 0.14)));
    k.add(box, WOOD[2] as string, place(0, 1.42, -0.2, 0, new THREE.Vector3(1.0, 0.14, 0.4)));
    k.add(box, IRON, place(0, 1.0, 0.1, 0, new THREE.Vector3(0.36, 0.36, 0.7)));
    k.add(new THREE.CylinderGeometry(0.28, 0.28, 0.08, 16), '#5b6068', place(0, 1.0, -0.32, 0, 1, Math.PI / 2));
    if (mark) for (const dx of [-0.3, 0.3]) k.add(box, '#ff4f3a', place(dx, 1.5, -0.2, 0, new THREE.Vector3(0.12, 0.3, 0.42)));
    const fm = k.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.2, flatShading: true })), 'tormo-drill');
    if (fm) {
      this.geos.push(fm.geometry);
      g.add(fm);
    }
    const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.5, 0.5)), this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(force), '#1b1f25')) })));
    plate.position.set(0, 1.42, 0.01);
    plate.rotation.x = -0.5;
    plate.position.z = 0.02;
    g.add(plate);
    const auger = new THREE.Group();
    const ak = new Kit();
    ak.add(new THREE.ConeGeometry(0.2, 0.95, 10), '#9aa3ae', place(0, 0, 0.0, 0, 1, Math.PI / 2, 0));
    for (let i = 0; i < 4; i++) ak.add(box, '#cfd5dc', place(0, 0, -0.32 + i * 0.16, (i * Math.PI) / 2, new THREE.Vector3(0.36 - i * 0.06, 0.04, 0.05)));
    const am = ak.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.35, metalness: 0.6 })), 'tormo-auger');
    if (am) {
      this.geos.push(am.geometry);
      auger.add(am);
    }
    auger.position.set(0, 1.0, 0.85);
    g.add(auger);
    const lever = new THREE.Group();
    const lk = new Kit();
    lk.add(box, IRON, place(0, 0.4, 0, 0, new THREE.Vector3(0.07, 0.8, 0.07)));
    lk.add(box, '#ff4f3a', place(0, 0.82, 0, 0, new THREE.Vector3(0.16, 0.16, 0.16)));
    const lm = lk.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6 })), 'tormo-clutch');
    if (lm) {
      this.geos.push(lm.geometry);
      lever.add(lm);
    }
    lever.position.set(0.62, 0, -0.55);
    g.add(lever);
    box.dispose();
    return { g, auger, lever, tip: new THREE.Vector3(), spin: 0 };
  }

  private buildDrills(kit: Kit, hot: Kit, s: BattleState, L: ShellLayout): void {
    const r = s.rules?.senda?.shell;
    if (!r) return;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const t = this.ctx.at(r.tormoAt.x, r.tormoAt.y, 0);
    const aim = (d: DrillView, at: THREE.Vector3): void => {
      d.g.position.copy(at);
      d.g.rotation.y = Math.atan2(t.x - at.x, t.z - at.z);
      d.g.updateMatrixWorld(true);
      d.tip.copy(d.auger.getWorldPosition(new THREE.Vector3())).add(new THREE.Vector3(t.x - at.x, 0, t.z - at.z).normalize().multiplyScalar(0.5));
    };
    r.drills.forEach((x, i) => {
      const d = this.drillModel(x.force, i === r.gang);
      aim(d, this.ctx.at(x.at.x, x.at.y, 0));
      this.group.add(d.g);
      this.drills.push(d);
    });
    // The chest's drill before him, its red strap like its gang's; a belt from it to that one.
    const c = this.drillModel(r.chestForce, true);
    aim(c, this.ctx.at(r.chestAt.x, r.chestAt.y, 0));
    this.group.add(c.g);
    this.chest = c;
    const gang = this.drills[r.gang];
    if (gang) {
      const a = gang.g.position.clone().setY(1.5);
      const bb = c.g.position.clone().setY(1.5);
      const mid = a.clone().lerp(bb, 0.5);
      hot.add(box, '#ff6a4a', place(mid.x, 1.55, mid.z, Math.atan2(-(bb.z - a.z), bb.x - a.x), new THREE.Vector3(a.distanceTo(bb), 0.05, 0.05)));
    }
    // The shaft from the mill over the line (on posts) and the belts' station: two pulleys a belt, straight or crossed.
    const mill = this.hex(L.mill.q, L.mill.r, 0);
    const mg = new THREE.Group();
    const mk = new Kit();
    mk.add(box, WOOD[0] as string, place(0, 1.6, 0, 0, new THREE.Vector3(0.9, 3.2, 0.9)));
    mk.add(new THREE.ConeGeometry(0.75, 0.8, 4), '#585e67', place(0, 3.6, 0, Math.PI / 4));
    const mm = mk.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, flatShading: true })), 'tormo-mill');
    if (mm) {
      this.geos.push(mm.geometry);
      mg.add(mm);
    }
    const sails = new THREE.Group();
    const sk = new Kit();
    for (let i = 0; i < 4; i++) sk.add(box, i % 2 ? '#cfd5dc' : '#b8bfc8', place(Math.cos((i * Math.PI) / 2) * 0.9, Math.sin((i * Math.PI) / 2) * 0.9, 0, 0, new THREE.Vector3(i % 2 ? 0.4 : 1.8, i % 2 ? 1.8 : 0.4, 0.04)));
    const sm = sk.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, side: THREE.DoubleSide })), 'tormo-sails');
    if (sm) {
      this.geos.push(sm.geometry);
      sails.add(sm);
    }
    sails.position.set(0, 2.8, 0.55);
    mg.add(sails);
    mg.position.copy(mill);
    this.group.add(mg);
    this.mill = sails;
    // The drive line from the mill along the floor (an iron shaft in a groove) to each drill's foot.
    for (const x of [...this.drills, ...(this.chest ? [] : [])]) {
      const a = x.g.position.clone();
      const m = mill.clone();
      const mid = a.clone().lerp(m, 0.5);
      kit.add(box, '#2c3036', place(mid.x, 0.03, mid.z, Math.atan2(-(m.z - a.z), m.x - a.x), new THREE.Vector3(a.distanceTo(m), 0.04, 0.22)));
      kit.add(cyl, '#6a7078', place(mid.x, 0.09, mid.z, Math.atan2(-(m.z - a.z), m.x - a.x), new THREE.Vector3(0.09, a.distanceTo(m), 0.09), 0, Math.PI / 2));
    }
    L.levers.forEach((h, i) => {
      const p = this.hex(h.q, h.r, 0);
      kit.add(box, IRON, place(p.x, 0.12, p.z, 0, new THREE.Vector3(0.8, 0.24, 0.6)));
      const mk2 = (crossed: boolean): THREE.Group => {
        const g = new THREE.Group();
        const k = new Kit();
        k.add(cyl, '#5b6068', place(-0.32, 1.2, 0, 0, new THREE.Vector3(0.36, 0.08, 0.36), Math.PI / 2, 0));
        k.add(cyl, '#5b6068', place(0.32, 0.6, 0, 0, new THREE.Vector3(0.36, 0.08, 0.36), Math.PI / 2, 0));
        if (crossed) {
          k.add(box, '#3a2c24', place(0, 0.9, 0.05, 0, new THREE.Vector3(0.05, 0.86, 0.04), 0, 0.85));
          k.add(box, '#3a2c24', place(0, 0.9, -0.05, 0, new THREE.Vector3(0.05, 0.86, 0.04), 0, -0.85));
        } else {
          k.add(box, '#3a2c24', place(-0.15, 0.9, 0, 0, new THREE.Vector3(0.05, 0.7, 0.04), 0, 0.5));
          k.add(box, '#3a2c24', place(0.15, 0.9, 0, 0, new THREE.Vector3(0.05, 0.7, 0.04), 0, 0.5));
        }
        const m = k.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7 })), 'tormo-belt');
        if (m) {
          this.geos.push(m.geometry);
          g.add(m);
        }
        g.position.copy(p);
        this.group.add(g);
        return g;
      };
      const lever = new THREE.Group();
      const lk = new Kit();
      lk.add(box, IRON, place(0, 0.4, 0, 0, new THREE.Vector3(0.08, 0.8, 0.08)));
      lk.add(box, '#ff4f3a', place(0, 0.84, 0, 0, new THREE.Vector3(0.18, 0.18, 0.18)));
      const lm = lk.build(this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6 })), 'tormo-belt-lever');
      if (lm) {
        this.geos.push(lm.geometry);
        lever.add(lm);
      }
      lever.position.set(p.x + 0.45, 0.2, p.z + 0.3);
      this.group.add(lever);
      this.belts.push({ straight: mk2(false), crossed: mk2(true), lever });
      void i;
    });
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the epilogue: Grada's top ledge under the Raja ---------- */

  private buildRaja(kit: Kit, far: Kit, hot: Kit): void {
    const R = this.map.radius - 1;
    const cells: { q: number; r: number }[] = [];
    for (let r = -4; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) cells.push({ q, r });
    this.setOpen(cells);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const wz = this.hex(0, -5, 0).z + 0.1;
    this.coursed(far, wz - 0.9, -1, 18, 44);
    // The Raja just over Tormo's shoulder: black, red deep in it, and the shoring of Grada's stone going up beside him.
    for (let x = -14; x < 14; x += 0.7) {
      const yy = 2.9 + Math.sin(x * 1.9) * 0.25 + Math.sin(x * 0.37) * 0.45;
      const wid = 0.3 + (Math.abs(x) < 4 ? 0.45 : 0.15);
      kit.add(box, '#06070a', place(x, yy, wz + 0.12, 0, new THREE.Vector3(0.9, wid, 0.12), 0, Math.cos(x * 1.9) * 0.5));
      if (Math.floor(x / 0.7) % 4 === 0) hot.add(box, '#7a1a0e', place(x, yy, wz + 0.16, 0, new THREE.Vector3(0.6, wid * 0.35, 0.05), 0, Math.cos(x * 1.9) * 0.5));
    }
    for (const x of [-2.4, 2.4]) for (let y = 0; y < 4; y++) kit.add(box, ['#8a9098', '#7e848c'][y % 2] as string, place(x, 0.5 + y, wz + 0.9, 0, new THREE.Vector3(1.1, 0.9, 0.8)));
    // The houses below, ledge by ledge down the wall to the south (their doors warm, people out on the ledges).
    for (let li = 0; li < 3; li++) {
      const ly = -2.6 - li * 2.6;
      const lz = 6 + li * 2.2;
      kit.add(box, '#59606a', place(0, ly - 0.3, lz, 0, new THREE.Vector3(26, 0.5, 2.0)));
      for (let i = 0; i < 8; i++) {
        const hx = -10 + i * 2.8 + (li % 2) * 1.3;
        kit.add(box, ['#84888e', '#74787e', '#8e9298'][(i + li) % 3] as string, place(hx, ly + 0.55, lz - 0.4, 0, new THREE.Vector3(1.5, 1.1, 1.0)));
        hot.add(box, '#ffc89e', place(hx + 0.3, ly + 0.6, lz + 0.12, 0, new THREE.Vector3(0.28, 0.36, 0.03)));
        this.light(new THREE.Vector3(hx + 0.3, ly + 0.6, lz + 0.4), WARM, 1.0, 0.45, 0.4);
      }
    }
    // The board's south edge: a parapet.
    for (let x = -9; x <= 9; x += 1.2) kit.add(box, '#848b94', place(x, 0.3, this.hex(0, R, 0).z + 0.7, 0, new THREE.Vector3(1.1, 0.6, 0.3)));
    box.dispose();
  }

  /* ---------- the live Tormo ---------- */

  private liveTormo(s: BattleState): void {
    const flags = s.rules?.senda?.plates ? [] : [];
    void flags;
    const old = (s.rules?.senda?.plates?.order.length ?? 6) === 7;
    const T = buildTormo({ eyes: 'dark', apart: true, ...(old ? { dust: 'old' as const } : {}) });
    // (On the wall, far up the frame, twice the size: seen from the ledge at the game's camera he read as a box.)
    T.root.scale.setScalar(SCALE.hero * (this.kind === 'torno' ? 2 : this.kind === 'plates' ? 2.2 : 1));
    this.group.add(T.root);
    this.T = T;
    // (A little light of his own: his dark iron, in the yard's shadow at the game's camera, read as a black box.)
    T.root.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if ((o as THREE.Mesh).isMesh && m?.isMeshStandardMaterial) m.emissive.set(this.kind === 'knees' ? '#000000' : '#141a20');
    });
    // The plates hidden but where his fight lights them; the stones on in his first three acts.
    for (const p of T.plates) p.group.visible = this.kind === 'plates';
    for (const st of T.stones) st.visible = this.kind !== 'plates';
    // The heart's cracks (the shell): three red seams that show one by one.
    const cmat = this.track(new THREE.MeshBasicMaterial({ color: '#1a0805' }));
    for (let i = 0; i < 3; i++) {
      const c = new THREE.Mesh(this.track(new THREE.BoxGeometry(1, 1, 1)), cmat);
      c.position.set(-0.08 + i * 0.08, 2.12 + (i - 1) * 0.14, 0.56);
      c.scale.set(0.03, 0.22, 0.02);
      c.rotation.z = (i - 1) * 0.7;
      c.visible = false;
      T.pivots.body.children[0]?.add(c);
      this.heartCracks.push(c);
    }
    if (this.kind === 'torno') {
      // On the wall, his back to the ledge, his hands up in the rock.
      T.root.rotation.y = 0;
      T.pivots.armL.rotation.x = -2.7;
      T.pivots.armR.rotation.x = -2.5;
      T.pivots.legL.rotation.x = -0.5;
      T.pivots.shinL.rotation.x = 0.6;
    }
    if (this.kind === 'shell') this.kneel(T, 0.9);
  }

  /** Down on his knees (`k` 0 standing to 1 knelt). */
  private kneel(T: TormoBuild, k: number): void {
    T.pivots.legL.rotation.x = -1.45 * k;
    T.pivots.shinL.rotation.x = 1.5 * k;
    T.pivots.legR.rotation.x = -0.25 * k;
    T.pivots.shinR.rotation.x = 1.55 * k;
    T.pivots.figure.position.y = -0.62 * k;
    T.pivots.body.rotation.x = 0.08 * k;
  }

  /** Whether the sim's enemy is Tormo (the layer draws him, the board doesn't). */
  owns(id: number): boolean {
    return this.kind === 'plates' && id === this.bossId && id > 0;
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      // (The yard's ground is only seen in the joints between its flags: dark.)
      const floor = this.cave ? ['#646b75', '#5c636d', '#6c737d'] : this.kind === 'torno' ? ['#5c636c', '#545b64', '#656c75'] : this.kind === 'raja' ? ['#6a7079', '#626870', '#737981'] : ['#3c4148', '#383d44', '#41464d'];
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        c.set(floor[Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        // The yard's flags: a joint every hex (worked stone, not ground).
        if (this.kind === 'raja' && (Math.abs(Math.sin(v.x * 2.6)) < 0.05 || Math.abs(Math.sin(v.z * 2.6)) < 0.05)) c.multiplyScalar(0.8);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    if (terrain.fog) terrain.fog.group.visible = false;
    if (!this.cave) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(this.sky);
  }

  paint(sky: Sky = this.sky): void {
    this.sky = sky;
    const look = this.cave ? LOOK.cave : this.kind === 'torno' || this.kind === 'raja' ? (sky === 'night' ? LOOK.night : LOOK.wall) : sky === 'night' ? LOOK.night : sky === 'dusk' ? LOOK.dusk : this.kind === 'plates' || this.kind === 'shell' ? LOOK.yardFight : LOOK.morning;
    if (!look) return;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    if (this.dome) {
      if (sky === 'night') {
        this.dome.set('#121a2c', '#3a4660', '#e8f0ff');
        this.dome.u.uSunDir.value.set(-0.4, 0.55, -0.7);
      } else if (sky === 'dusk') {
        this.dome.set('#3c4866', '#b4aab4', '#ffd0b0');
        this.dome.u.uSunDir.value.set(0.7, 0.12, -0.6);
      } else {
        this.dome.set('#5a7092', '#c8d2de', '#fff0e0');
        this.dome.u.uSunDir.value.set(0.5, 0.45, -0.6);
      }
    }
  }

  /* ---------- each tick ---------- */

  tick(s: BattleState): void {
    const T = this.T;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const at = (h = 0.2): THREE.Vector3 => this.ctx.at(e.x, e.y, h);
      switch (e.kind) {
        case 'kneeSpark': {
          const tv = this.trains[Math.floor((e.n ?? 0) / KNEE_AXLES)];
          if (tv) {
            tv.spark[(e.n ?? 0) % KNEE_AXLES] = 1;
            const p = tv.group.getWorldPosition(new THREE.Vector3());
            this.ctx.vfx.burst(p, { n: 26, color: '#ffd0a0', speed: 2.6, up: 1.6, size: 0.08, life: 12, gravity: 1 });
            this.ctx.flare(p, '#ffb48a', 2.6, 10);
          }
          this.ctx.shake(0.3);
          break;
        }
        case 'kneeLock': {
          const tv = this.trains[e.n ?? 0];
          if (tv) {
            tv.locked = true;
            tv.flash = 1;
            const p = tv.group.getWorldPosition(new THREE.Vector3());
            this.ctx.vfx.burst(p, { n: 34, color: '#ff8a6e', speed: 3, up: 2, size: 0.1, life: 14, gravity: 1 });
            this.ctx.vfx.ring(p.clone().setY(0.08), 0.3, 2.0, '#ff4f3a', 12, 0.9);
            this.ctx.flare(p, '#ffffff', 4, 14);
          }
          this.ctx.shake(0.5);
          break;
        }
        case 'quakeWarn':
          this.ctx.shake(0.2);
          break;
        case 'quake':
          // The hall shakes hard (the more joints loose, the harder): dust off every wall, a wave out from him.
          this.ctx.shake(0.4 + 0.2 * (e.n ?? 0));
          this.ctx.vfx.ring(at(0.08), 0.6, 9, '#b9c0c9', 22, 0.7);
          for (const d of this.quakeDust) this.ctx.vfx.burst(d.curtain.position.clone().setY(0.3), { n: 8, color: '#8a8f96', speed: 1.6, up: 1, size: 0.3, life: 22, smoke: true });
          break;
        case 'kneesDown':
          this.ctx.shake(1.2);
          this.ctx.vfx.burst(at(0.4), { n: 60, color: '#8a8f96', speed: 3, up: 2.4, size: 0.3, life: 26, smoke: true });
          break;
        case 'stomp':
        case 'stamp':
          this.ctx.shake(0.9);
          this.ctx.vfx.ring(at(0.08), 0.6, 3.2, '#ffb48a', 14, 0.8);
          this.ctx.vfx.burst(at(0.2), { n: 30, color: '#8a8f96', speed: 3, up: 1.4, size: 0.24, life: 18, smoke: true });
          break;
        case 'pulse':
          this.ctx.shake(0.3);
          this.ctx.vfx.burst(at(0.3), { n: 26, color: '#9a9fa6', speed: 2.6, up: 2.4, size: 0.16, life: 16, gravity: 1 });
          this.ctx.flare(at(0.4), '#ffd8c4', 2.2, 10);
          break;
        case 'thaw':
        case 'cubsOut':
          this.ctx.vfx.burst(at(0.5), { n: 22, color: '#4a4644', speed: 1.6, up: 1.4, size: 0.4, life: 22, smoke: true });
          break;
        case 'take':
        case 'put':
          this.ctx.vfx.burst(at(1.0), { n: 10, color: '#ffd0a0', speed: 1.2, up: 1.0, size: 0.06, life: 8, gravity: 1 });
          break;
        case 'haul':
          this.pull = 1;
          this.ctx.flare(this.drumAt.clone(), '#ffffff', 3, 12);
          break;
        case 'slip':
          this.pull = 1;
          this.slip = 1;
          this.ctx.shake(0.6);
          this.ctx.vfx.burst(this.drumAt.clone(), { n: 30, color: '#ffd0a0', speed: 3, up: 1.6, size: 0.08, life: 12, gravity: 1 });
          break;
        case 'pullLoose':
          this.pull = 1;
          break;
        case 'climb':
          this.ctx.shake(0.25);
          break;
        case 'fray':
          this.ctx.vfx.burst(this.drumAt.clone().setY(1.6), { n: 6, color: '#c9ccd0', speed: 1, up: 0.5, size: 0.05, life: 8 });
          break;
        case 'tornoFree':
          this.ctx.shake(1.0);
          break;
        case 'clutchIn':
        case 'clutchOut':
        case 'belt':
          this.ctx.vfx.burst(at(1.0), { n: 10, color: '#ffd0a0', speed: 1.4, up: 1.2, size: 0.06, life: 8, gravity: 1 });
          break;
        case 'jam':
          this.ctx.shake(0.9);
          for (const d of this.drills) this.ctx.vfx.burst(d.g.position.clone().setY(1.2), { n: 16, color: '#ffd0a0', speed: 3, up: 1.6, size: 0.08, life: 10, gravity: 1 });
          break;
        case 'crack':
          this.hurt = 1;
          this.ctx.shake(0.8);
          for (let i = 0; i < this.heartCracks.length; i++) (this.heartCracks[i] as THREE.Mesh).visible = i < (e.n ?? 0);
          if (this.chest) this.ctx.vfx.burst(this.chest.tip.clone(), { n: 30, color: '#ff6a4a', speed: 2.8, up: 1.8, size: 0.09, life: 14, gravity: 1 });
          if (T) this.ctx.flare(this.ctx.at(e.x, e.y, 2.2), '#ff4f3a', 5, 18);
          break;
        case 'stoneDown': {
          const k = e.n ?? 0;
          this.outStone[k] = true;
          const st = T?.stones[k];
          if (st) {
            const p = st.getWorldPosition(new THREE.Vector3());
            const box = st.children[0] as THREE.Mesh | undefined;
            const c = box?.geometry.boundingSphere?.center;
            const w = c ? box.localToWorld(c.clone()) : p;
            this.ctx.vfx.burst(w, { n: 50, color: '#868c94', speed: 3.4, up: 3, size: 0.24, life: 24, gravity: 1 });
            this.ctx.flare(w, '#ffffff', 5, 16);
            st.visible = false;
            // The plate under it shows (dim: what got in glows there now).
            const pl = T?.plates[k === 0 ? 3 : k === 1 ? 4 : 5];
            if (pl) {
              pl.group.visible = true;
              pl.rim.color.set('#7a2416');
              pl.core.color.set('#4a1a12');
            }
          }
          this.ctx.shake(1.0);
          break;
        }
        case 'twistWarn':
          this.ctx.shake(0.3);
          break;
        case 'twist':
          this.ctx.shake(0.6);
          this.ctx.vfx.ring(at(0.08), 0.6, 2.8, '#ff8a6e', 12, 0.7);
          break;
        case 'drillStall': {
          const d = this.drills[e.n ?? 0];
          if (d) this.ctx.vfx.burst(d.tip.clone(), { n: 20, color: '#4a4644', speed: 2, up: 1.4, size: 0.2, life: 14, smoke: true });
          break;
        }
        case 'plateOut':
          this.ctx.shake(1.0);
          this.ctx.vfx.burst(at(2.0), { n: 50, color: '#121417', speed: 2.6, up: 2.6, size: 0.3, life: 30, smoke: true });
          this.ctx.flare(at(2.2), '#ff4f3a', 6, 20);
          break;
        case 'plateMove':
          this.ctx.flare(at(2.2), '#ff8a6e', 3, 12);
          break;
        case 'bolt': {
          const bw = this.bows[e.n ?? 0];
          if (bw) {
            bw.kick = 1;
            this.ctx.vfx.shaft(bw.at.clone(), at(2.0), '#e8eef4', 4, 0.06);
          }
          break;
        }
        case 'bowJam': {
          const bw = this.bows[e.n ?? 0];
          if (bw) {
            bw.jam = 1;
            this.ctx.vfx.burst(bw.at.clone(), { n: 24, color: '#8a8f96', speed: 2, up: 1.6, size: 0.24, life: 18, smoke: true });
          }
          break;
        }
        case 'fist':
          this.ctx.shake(0.6);
          this.ctx.vfx.burst(at(0.2), { n: 26, color: '#9a9fa6', speed: 2.6, up: 1.6, size: 0.16, life: 14, gravity: 1 });
          break;
        case 'hammer':
          this.ctx.shake(1.4);
          this.ctx.vfx.ring(at(0.1), 0.6, 4.0, '#ff4f3a', 16, 0.9);
          this.ctx.vfx.burst(at(0.3), { n: 60, color: '#8a8f96', speed: 3.6, up: 2.2, size: 0.3, life: 22, smoke: true });
          break;
        case 'tormoFree':
          this.ctx.shake(0.8);
          this.ctx.vfx.burst(at(2.2), { n: 80, color: '#0c0d10', speed: 2.4, up: 3, size: 0.4, life: 40, smoke: true });
          break;
        case 'iron':
          this.hurt = Math.max(this.hurt, 0.5);
          break;
        default:
          break;
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
    const w = s.senda?.plates;
    if (w) this.bossId = w.id;
  }

  /* ---------- each frame ---------- */

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    const s = this.last;
    this.glow.begin();
    let disc = 0;
    let ring = 0;
    const showDisc = (x: number, y: number, r: number, left: number, color: THREE.Color): void => {
      const d = this.discs[disc++];
      if (!d) return;
      const p = this.ctx.at(x, y, 0.05);
      d.mesh.visible = true;
      d.mesh.position.copy(p);
      d.mesh.scale.setScalar(r * K * (0.35 + 0.65 * (1 - left)));
      d.mat.color.copy(color);
      d.mat.opacity = 0.22 + 0.36 * (1 - left) + 0.08 * Math.sin(now * 16);
    };
    const showRing = (x: number, y: number, inR: number, outR: number, left: number, color: THREE.Color): void => {
      const d = this.rings[ring++];
      if (!d) return;
      const p = this.ctx.at(x, y, 0.06);
      d.mesh.visible = true;
      d.mesh.position.copy(p);
      d.mesh.scale.setScalar(outR * K);
      // (The ring's band reaches in to `inR`: its geometry is 0.78 to 1; a smaller inner edge widens it by scale.)
      d.mat.color.copy(color);
      d.mat.opacity = 0.3 + 0.4 * (1 - left) + 0.1 * Math.sin(now * 14);
      void inR;
    };
    if (s) {
      if (this.kind === 'knees') this.frameKnees(s, now, dt, showRing);
      else if (this.kind === 'torno') this.frameTorno(s, now, dt);
      else if (this.kind === 'shell') this.frameShell(s, now, dt, showRing);
      else if (this.kind === 'plates') this.framePlates(s, now, dt, showDisc, showRing);
      // The spots coming (stone off the roof or the Raja, the shards, the yard's edge): a disc filling as it nears.
      const sn = s.senda;
      const sr = s.rules?.senda;
      const spots = sn?.knees?.spots ?? sn?.torno?.spots ?? sn?.shell?.spots ?? sn?.plates?.spots ?? [];
      const warn = sr?.knees?.debris.warn ?? sr?.torno?.rocks.warn ?? sr?.shell?.shards.warn ?? sr?.plates?.rocks.warn ?? 36;
      const rr = sr?.knees?.debris.r ?? sr?.torno?.rocks.r ?? sr?.shell?.shards.r ?? sr?.plates?.rocks.r ?? 1000;
      let fi = 0;
      // (Where it comes from: the hall's roof, a little over the walls' tops; the cliff over the yard, higher.)
      const fromY = this.kind === 'knees' ? 5.2 : 8;
      for (const p of spots) {
        const left = Math.max(0, (p.t - s.tick) / Math.max(1, warn));
        showDisc(p.x, p.y, rr, left, RED);
        const at = this.ctx.at(p.x, p.y, 0.2);
        this.glow.add(at.x, 0.25, at.z, RED, rr * K * 2 * (1 - left * 0.6), 0.25 + 0.3 * (1 - left));
        const f = this.falling[fi++];
        if (f) {
          // Shaking loose up there, then down in the last third (eased in: it falls).
          const drop = left < 0.33 ? 1 - left / 0.33 : 0;
          const y = at.y + 0.3 + (fromY - 0.3) * (1 - drop * drop);
          const sz = Math.min(1.6, rr * K * 1.2);
          const shake = drop > 0 ? 0 : Math.sin(now * 40 + fi) * 0.05;
          f.stone.visible = true;
          f.stone.position.set(at.x + shake, y, at.z);
          f.stone.scale.set(sz, sz * 0.8, sz);
          f.stone.rotation.set(now * 2 * drop + fi, fi * 1.3, 0);
          f.dust.visible = true;
          const top = fromY + 1.5;
          f.dust.position.set(at.x, (y + top) / 2, at.z);
          f.dust.scale.set(1 + sz, Math.max(0.1, top - y), 1 + sz);
        }
      }
      for (let i = fi; i < this.falling.length; i++) {
        const f = this.falling[i] as { stone: THREE.Mesh; dust: THREE.Mesh };
        f.stone.visible = false;
        f.dust.visible = false;
      }
      if (this.dustMat) this.dustMat.opacity = 0.32 + 0.06 * Math.sin(now * 9);
    }
    for (let i = disc; i < this.discs.length; i++) (this.discs[i] as { mesh: THREE.Mesh }).mesh.visible = false;
    for (let i = ring; i < this.rings.length; i++) (this.rings[i] as { mesh: THREE.Mesh }).mesh.visible = false;
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    // The heart, red through his chest; the hurt's flash.
    if (this.T) {
      this.hurt = Math.max(0, this.hurt - dt * 1.5);
      const beat = 0.7 + 0.3 * Math.sin(now * 3.2);
      this.T.heart.color.setRGB(1, 0.31 * beat + this.hurt * 0.5, 0.23 * beat + this.hurt * 0.4);
      this.T.tube.color.setRGB(0.85, 0.2 + 0.08 * Math.sin(now * 2.2), 0.12);
    }
    this.glow.end();
  }

  /** Walks the live Tormo to a world point facing `yaw`, his legs striding by the ground he covers. */
  private walk(p: THREE.Vector3, yaw: number, dt: number, still = false): void {
    const T = this.T;
    if (!T) return;
    const moved = this.lastXY ? this.lastXY.distanceTo(p) : 0;
    this.lastXY = p.clone();
    T.root.position.copy(p);
    // The turn eased (he is heavy).
    let d = yaw - this.yaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    this.yaw += d * Math.min(1, dt * 6);
    T.root.rotation.y = this.yaw;
    if (!still && moved > 0.0005) this.stride += moved * 2.2;
    const sw = still ? 0 : Math.sin(this.stride);
    T.pivots.legL.rotation.x = sw * 0.42;
    T.pivots.legR.rotation.x = -sw * 0.42;
    T.pivots.shinL.rotation.x = Math.max(0, -sw) * 0.5;
    T.pivots.shinR.rotation.x = Math.max(0, sw) * 0.5;
    T.pivots.armL.rotation.x = -sw * 0.25;
    T.pivots.armR.rotation.x = sw * 0.25;
    T.pivots.figure.position.y = Math.abs(sw) * 0.04;
  }

  private frameKnees(s: BattleState, now: number, dt: number, showRing: (x: number, y: number, inR: number, outR: number, left: number, c: THREE.Color) => void): void {
    const r = s.rules?.senda?.knees;
    const w = s.senda?.knees;
    const T = this.T;
    if (!r || !w || !T) return;
    // The gear in the hero's hands, over its head.
    if (this.handGear) {
      const hp = s.heroes[0] ? this.ctx.pos(s.heroes[0].id) : undefined;
      this.handGear.visible = !!w.hand && !!hp;
      if (hp) this.handGear.position.copy(hp).setY(hp.y + 1.45);
      this.handGear.rotation.z = now * 2;
    }
    const p = this.ctx.at(w.x, w.y, 0);
    const winding = w.stompAt >= 0;
    const down = w.down >= 0;
    this.walk(p, facingToRotY(w.face), dt, winding || down || s.tick < w.still);
    if (winding) {
      // One leg up, the arms out: the stomp comes.
      const k = 1 - Math.max(0, (w.stompAt - s.tick) / Math.max(1, r.stomp.warn));
      T.pivots.legR.rotation.x = -0.9 * Math.min(1, k * 2);
      T.pivots.shinR.rotation.x = 0.9 * Math.min(1, k * 2);
      T.pivots.armL.rotation.z = -0.4;
      T.pivots.armR.rotation.z = 0.4;
      showRing(w.x, w.y, r.stomp.inR, r.stomp.outR, 1 - k, RED);
    } else {
      T.pivots.armL.rotation.z = 0;
      T.pivots.armR.rotation.z = 0;
    }
    if (down) this.kneel(T, Math.min(1, (s.tick - w.down) / 18));
    // The tremor warned: the dust comes down thicker as it nears, pebbles with it; the loose joints beat red (each one
    // loose is what makes it hurt).
    const q = w.quakeAt >= 0 ? Math.min(1, 1 - (w.quakeAt - s.tick) / Math.max(1, r.quake.warn)) : 0;
    if (this.quakeMat) this.quakeMat.opacity = q > 0 ? 0.12 + 0.26 * q + 0.05 * Math.sin(now * 23) : 0;
    for (const d of this.quakeDust) {
      d.curtain.visible = q > 0;
      d.pebble.visible = q > 0;
      if (q <= 0) continue;
      const f = (now * (0.9 + d.seed) + d.seed * 3) % 1;
      d.pebble.position.y = d.top * (1 - f * f);
      d.pebble.rotation.set(now * 5 + d.seed, now * 3, 0);
      d.curtain.position.x += Math.sin(now * 31 + d.seed * 9) * 0.004 * q;
    }
    // The joints' trains: by his knees and elbows, tilted up to the camera; turning as he walks unless locked.
    const walking = !winding && !down && s.tick >= w.still;
    const fwd = new THREE.Vector3(Math.cos((w.face / 1024) * Math.PI * 2), 0, Math.sin((w.face / 1024) * Math.PI * 2));
    const up = new THREE.Vector3(0, 1, 0);
    r.joints.forEach((J, j) => {
      const tv = this.trains[j];
      if (!tv) return;
      const jp = kneeJointAt(r, w, j);
      const side = J.off.x < 0 ? -1 : 1;
      const right = new THREE.Vector3().crossVectors(up, fwd).multiplyScalar(-1);
      const out = right.clone().multiplyScalar(side);
      const normal = out.clone().multiplyScalar(0.45).addScaledVector(up, 0.9).normalize();
      const xAxis = fwd.clone();
      const yAxis = new THREE.Vector3().crossVectors(normal, xAxis).normalize();
      const m = new THREE.Matrix4().makeBasis(xAxis, yAxis, normal);
      tv.group.quaternion.setFromRotationMatrix(m);
      const h = j < 2 ? 0.95 : 2.0;
      tv.group.position.copy(this.ctx.at(jp.x, jp.y, h)).addScaledVector(out, j < 2 ? 0.55 : 0.45);
      if (down && j < 2) tv.group.position.y -= 0.5;
      const locked = !!w.locked[j];
      tv.locked = locked;
      if (walking && !locked) tv.spin += dt * 2.4;
      tv.gears.forEach((g, i) => {
        g.rotation.z = tv.spin * kneeGearWay(r, j, i) * (i % 2 ? 1.1 : 1);
      });
      tv.arrow.rotation.z = tv.spin * J.drive;
      tv.flash = Math.max(0, tv.flash - dt * 1.2);
      tv.lockMat.color.set(locked ? '#ff4f3a' : '#3a3f46');
      tv.lockMat.emissive.set(locked ? '#8a1e10' : '#000000');
      tv.pins.forEach((pin, k) => {
        const tried = ((w.tried[j] ?? 0) & (1 << k)) !== 0;
        pin.scale.set(0.09, locked && k === J.jam ? 0.3 : 0.14, 0.09);
        tv.spark[k] = Math.max(0, (tv.spark[k] ?? 0) - dt * 2);
        if (tried && !locked) pin.rotation.y = now * 6;
        const gp = pin.getWorldPosition(this.v);
        if ((tv.spark[k] ?? 0) > 0) this.glow.add(gp.x, gp.y, gp.z, WARM, 0.8, (tv.spark[k] ?? 0) * 0.8);
      });
      const gp = tv.group.getWorldPosition(this.v);
      if (locked) this.glow.add(gp.x, gp.y, gp.z, RED, 1.4 + tv.flash * 1.6, 0.35 + tv.flash * 0.4);
      else if (q > 0) this.glow.add(gp.x, gp.y, gp.z, DEEP, 1.3 + 0.5 * q, (0.25 + 0.35 * q) * (0.6 + 0.4 * Math.sin(now * 12)));
      else this.glow.add(gp.x, gp.y, gp.z, COLD, 1.1, 0.16);
      void kneeAxleAt;
    });
  }

  private frameTorno(s: BattleState, now: number, dt: number): void {
    const r = s.rules?.senda?.torno;
    const w = s.senda?.torno;
    const T = this.T;
    const L = this.TL;
    if (!r || !w || !T || !L) return;
    // Tormo on Grada's wall, higher a span nearer the Raja; down a little as the cable hauls; off it once free.
    const want = w.end >= 0 ? -4 : 1.6 + (r.spans - w.spans) * 2.6 - (w.hauled / Math.max(1, r.haul)) * 2.2;
    this.climbY += (want - this.climbY) * Math.min(1, dt * (w.end >= 0 ? 1.2 : 2.2));
    const tx = r.top.x * K;
    const tp = new THREE.Vector3(tx, this.climbY, this.wallZ + 1.7);
    T.root.position.copy(tp);
    T.root.rotation.y = Math.PI;
    // A cold light on him, so the iron reads against Grada's grey wall.
    this.glow.add(tp.x, tp.y + 2.4, tp.z + 0.8, COLD, 5.5, 0.22);
    const climbing = w.end < 0 && w.haul < 0;
    const c = Math.sin(now * 2.4) * (climbing ? 1 : 0.2);
    T.pivots.armL.rotation.x = -2.7 + c * 0.3;
    T.pivots.armR.rotation.x = -2.5 - c * 0.3;
    T.pivots.legL.rotation.x = -0.5 + c * 0.3;
    T.pivots.legR.rotation.x = -0.1 - c * 0.3;
    // The cable: from the drum to his harness, taut and bright while it hauls, else sagging a little.
    const cable = this.cable;
    if (cable && this.cableMat) {
      const a = this.drumAt.clone().setY(this.drumAt.y + 0.5);
      const bb = tp.clone().add(new THREE.Vector3(0, 2.0, 0.4));
      const taut = w.haul >= 0;
      this.slip = Math.max(0, this.slip - dt * 1.5);
      const mid = a.clone().lerp(bb, 0.5);
      mid.y -= taut ? 0 : 0.6 + 0.2 * Math.sin(now * 1.3);
      mid.y += Math.sin(now * 60) * 0.05 * this.slip;
      cable.position.copy(mid);
      const dir = bb.clone().sub(a);
      cable.scale.set(1, dir.length(), 1);
      cable.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
      this.cableMat.emissive.set(taut ? '#8a9bb0' : '#3a3f46');
      if (taut) this.glow.add(mid.x, mid.y, mid.z, COLD, 2.2, 0.18);
    }
    // The drum turning as it hauls; the lever pulled.
    if (this.drum && w.haul >= 0) this.drum.rotation.x -= dt * 1.2;
    this.pull = Math.max(0, this.pull - dt * 2);
    if (this.lever) this.lever.rotation.x = -0.9 * this.pull;
    // The gears: where they are (a slot, the shelf, the hero's hands), turning in the box while it hauls.
    const speed = tornoSpeed(r, w);
    const hero = s.heroes[0];
    const hp = hero ? this.ctx.pos(hero.id) : undefined;
    this.boxGears.forEach((g, k) => {
      const at = w.on[k] ?? -1;
      if (at < 0) {
        if (hp) g.position.copy(hp).setY(hp.y + 1.5);
      } else {
        const cell = at < 4 ? L.slots[at] : L.lie[at - 4];
        if (cell) g.position.copy(this.hex(cell.q, cell.r, at < 4 ? 1.05 : 0.65));
        if (at >= 4) g.position.z -= 0.3;
      }
      // A gear in the box turns at its axle's speed while it hauls (A the motor's, B the slower, C the slowest).
      if (at >= 0 && at < 4 && w.haul >= 0 && speed.den > 0) {
        const teeth = r.pieces[k] as number;
        const box = [0, 1, 2, 3].map((q) => r.pieces[tornoAt(w, q)] ?? 12);
        const sp = at === 0 ? 1 : at === 3 ? speed.num / speed.den / r.input : (box[0] as number) / (box[1] as number);
        this.gearSpin[k] = (this.gearSpin[k] ?? 0) + dt * 3 * sp * (at % 2 === 0 ? 1 : -1);
        void teeth;
      }
      g.rotation.z = this.gearSpin[k] ?? 0;
    });
    // The Raja's red brighter as he nears it.
    const near = 1 - w.spans / Math.max(1, r.spans);
    this.glow.add(tx, 9.4, this.wallZ + 1, RED, 6 + near * 6, 0.15 + near * 0.35);
  }

  private frameShell(s: BattleState, now: number, dt: number, showRing: (x: number, y: number, inR: number, outR: number, left: number, c: THREE.Color) => void): void {
    const r = s.rules?.senda?.shell;
    const w = s.senda?.shell;
    const T = this.T;
    if (!r || !w || !T) return;
    const tp = this.ctx.at(r.tormoAt.x, r.tormoAt.y, 0);
    T.root.position.copy(tp);
    // Facing the yard's south; turned a quarter toward the side he gives the line, writhing while he warns.
    const want = (w.side === 0 ? 0.55 : -0.55) + (w.twistAt >= 0 ? Math.sin(now * 9) * 0.12 : 0);
    this.twistYaw += (want - this.twistYaw) * Math.min(1, dt * 3);
    T.root.rotation.y = this.twistYaw;
    this.kneel(T, 0.9);
    T.pivots.armL.rotation.x = -0.3 + (w.twistAt >= 0 ? Math.sin(now * 7) * 0.4 : 0);
    T.pivots.armR.rotation.x = -0.3 - (w.twistAt >= 0 ? Math.sin(now * 7) * 0.4 : 0);
    if (w.twistAt >= 0) showRing(r.tormoAt.x, r.tormoAt.y, 0, r.twist.sweepR, Math.max(0, (w.twistAt - s.tick) / Math.max(1, r.twist.warn)), RED);
    // The drills: in, they turn (their arrow's way or back); stopped by a cub, still; their clutch lever down or up.
    r.drills.forEach((x, d) => {
      const v = this.drills[d];
      if (!v) return;
      const inn = !!w.inn[d];
      const stalled = s.tick < (w.stall[d] ?? 0);
      const right = shellTurnsRight(r, w, d);
      if (inn && !stalled) v.spin += dt * (right ? 9 : -6);
      v.auger.rotation.z = v.spin;
      v.lever.rotation.x = inn ? 0.9 : -0.3;
      if (inn && !stalled && right && (s.tick + d) % 3 === 0) this.ctx.vfx.burst(v.tip.clone(), { n: 2, color: '#a8aeb6', speed: 1.6, up: 1.0, size: 0.06, life: 8, gravity: 1 });
      if (inn) this.glow.add(v.tip.x, v.tip.y, v.tip.z, right ? WARM : RED, 0.7, 0.35);
    });
    // The chest's drill: in with its gang, turning the other way (cutting when the gang turns back).
    const c = this.chest;
    if (c) {
      const inn = !!w.inn[r.gang];
      const cuts = inn && !shellTurnsRight(r, w, r.gang);
      if (inn) c.spin += dt * (cuts ? 9 : -6);
      c.auger.rotation.z = c.spin;
      if (cuts) this.glow.add(c.tip.x, c.tip.y, c.tip.z, RED, 1.6, 0.6 + 0.2 * Math.sin(now * 30));
    }
    // The belts: straight or crossed by their lever.
    this.belts.forEach((b, i) => {
      const turned = (w.belts[i] ?? 0) === 1;
      b.straight.visible = !turned;
      b.crossed.visible = turned;
      b.lever.rotation.z = turned ? 0.7 : -0.7;
    });
    // The mill's sails: turning while the shaft runs, slower the more is asked.
    const ask = r.drills.reduce((a, x, d) => a + (w.inn[d] ? x.force : 0), 0);
    this.millSpin += dt * (ask > 0 ? 1.4 - ask * 0.25 : 0.6);
    if (this.mill) this.mill.rotation.z = this.millSpin;
    // The stones that fell show their plates dim; the heart's glow brighter with its cracks.
    const hp = T.pivots.body.getWorldPosition(this.v);
    this.glow.add(hp.x, hp.y + 1.0, hp.z + 0.4, RED, 1.2 + w.cracks * 0.6, 0.2 + w.cracks * 0.15);
  }

  private framePlates(s: BattleState, now: number, dt: number, showDisc: (x: number, y: number, r: number, left: number, c: THREE.Color) => void, showRing: (x: number, y: number, inR: number, outR: number, left: number, c: THREE.Color) => void): void {
    const r = s.rules?.senda?.plates;
    const w = s.senda?.plates;
    const T = this.T;
    if (!r || !w || !T) return;
    const e = s.enemies.find((x) => x.id === w.id);
    if (!e) return;
    const drawn = this.ctx.pos(e.id);
    const p = drawn ? drawn.clone() : this.ctx.at(e.x, e.y, 0);
    p.y = this.ctx.ground(p.x, p.z);
    const winding = w.fist >= 0 || w.stampAt >= 0 || w.hammer >= 0;
    this.walk(p, facingToRotY(e.facing), dt, winding || e.anim !== 'walk');
    // Winding: the fist's arm up, the stamp's leg, both hands over his head for the hammer.
    if (w.fist >= 0) {
      T.pivots.armR.rotation.x = -2.6;
      showDisc(w.fistAt.x, w.fistAt.y, r.fist.r, Math.max(0, (w.fist - s.tick) / Math.max(1, r.fist.warn)), RED);
    }
    if (w.stampAt >= 0) {
      T.pivots.legL.rotation.x = -0.9;
      T.pivots.shinL.rotation.x = 0.9;
      showRing(e.x, e.y, 0, r.stamp.r, Math.max(0, (w.stampAt - s.tick) / Math.max(1, r.stamp.warn)), RED);
    }
    if (w.hammer >= 0) {
      T.pivots.armL.rotation.x = -2.9;
      T.pivots.armR.rotation.x = -2.9;
      T.pivots.armL.rotation.z = 0.3;
      T.pivots.armR.rotation.z = -0.3;
      showDisc(e.x, e.y, r.hammer.r, Math.max(0, (w.hammer - s.tick) / Math.max(1, r.hammer.warn)), RED);
    } else {
      T.pivots.armL.rotation.z = 0;
      T.pivots.armR.rotation.z = 0;
    }
    if (w.free >= 0) {
      T.eyes.color.set('#bfe6ff');
      this.walk(p, facingToRotY(e.facing), dt, true);
    }
    // The plates: the one that glows black with a red rim beating, a glow seen from anywhere; the rest to come dim, out ones grey.
    const out = new Set(r.order.slice(0, r.dark + w.lit));
    const ph = platesPhase(r, w);
    T.plates.forEach((pl, i) => {
      const lit = w.free < 0 && i === w.slot;
      const gone = out.has(i) && !lit;
      const coming = r.order.includes(i) && !gone;
      pl.group.visible = true;
      pl.group.scale.setScalar(lit ? 1.25 + 0.08 * Math.sin(now * 6) : 1);
      // The lit one glows from its middle out (its core's gradient), the rim the dragon's red; to come, an ember; out, iron.
      if (lit) {
        pl.rim.color.set(RED);
        const k = 0.86 + 0.14 * Math.sin(now * 6);
        pl.core.color.setRGB(k, k, k);
      } else if (coming) {
        // (Iron, not red: red rims on every plate read as six targets; only the lit one is the dragon's red.)
        pl.rim.color.set('#4a4f57');
        pl.core.color.set('#2a1612');
      } else {
        pl.rim.color.set('#3a3d42');
        pl.core.color.set('#16181b');
      }
      if (lit) {
        const gp = pl.group.getWorldPosition(this.v);
        // (Red, not white: two strong additive glows on one spot burnt out to cream.)
        this.glow.add(gp.x, gp.y, gp.z, DEEP, 2.6 + 0.5 * Math.sin(now * 6), 0.3);
      }
    });
    void ph;
    // The crossbows turn to him; bolting kicks them back; one hit by a rock sits askew, smoking.
    this.bows.forEach((b, i) => {
      const off = s.tick < (w.bowOff[i] ?? 0);
      const ang = Math.atan2(p.x - b.at.x, p.z - b.at.z);
      b.g.rotation.y = ang;
      b.kick = Math.max(0, b.kick - dt * 4);
      b.arm.position.z = -0.15 * b.kick;
      b.g.rotation.z = off ? 0.45 : 0;
      if (off && Math.floor(now * 3 + i) % 2 === 0) this.glow.add(b.at.x, b.at.y + 0.3, b.at.z, WARM, 0.6, 0.25);
    });
    void PLATE_FACE;
    void TORMO_PLATES;
  }

  dispose(): void {
    this.T?.dispose();
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
